import { sessionFetch } from './session-expiry';
import { gecisHatasiMetni } from './gecis-hatasi';

export type WebPaymentStatus = 'CREATED' | 'AUTHORIZED' | 'CAPTURED' | 'FAILED' | 'REFUNDED' | 'VOIDED';
export interface WebPaymentNotification {
  providerReportedType: string; receivedAt: string; signatureValid: boolean; confirmedWithProvider: boolean;
}
export interface WebPaymentRecord {
  id: string; reference: string; orderStatus: 'PENDING_PAYMENT' | 'PAID' | 'CANCELLED' | 'REFUNDED';
  paymentStatus: WebPaymentStatus; provider: string;
  customerName?: string | null; customerPhone?: string | null;
  amount: { amount: number; currency: string }; orderTotal: { amount: number; currency: string };
  createdAt: string; updatedAt: string; paidAt: string | null;
  lastNotification: WebPaymentNotification | null; lastFailureNotification: WebPaymentNotification | null;
}
export interface WebPayments { payments: WebPaymentRecord[]; page: { limit: number; offset: number; total: number } }
export interface WebPaymentsQuery { reference?: string; status?: WebPaymentStatus; notification?: 'failed'; from?: string; to?: string; limit?: number; offset?: number }

export interface Me {
  userId: string;
  displayName: string;
  role: string;
  permissions: string[];
}

export interface SiteSiparisSatiri {
  reference: string;
  state: 'PENDING_PAYMENT' | 'AUTHORIZED' | 'PAID' | 'CANCELLED' | 'PARTIALLY_REFUNDED' | 'REFUNDED';
  placedAt: string;
  paidAt: string | null;
  totalMinor: number | null;
  currencyCode: string | null;
}
export interface SiteSiparisOzetiVerisi {
  paid: SiteSiparisSatiri[];
  other: SiteSiparisSatiri[];
  limit: number;
  includeFinancial: boolean;
  generatedAt: string;
}

export interface WorkspaceCard {
  key: string;
  label: string;
  value: number | string;
  source: string;
}

export interface WorkspaceSummary {
  role: string;
  displayName: string;
  workspaceKey: string;
  permissions: string[];
  cards: WorkspaceCard[];
  sections: string[];
  recentCustomers: { id: string; displayName: string; statusCode: string; updatedAt: string }[];
  statusBreakdown: { statusCode: string; count: number }[];
}

export interface CustomerListItem {
  id: string;
  displayName: string;
  type: string;
  statusCode: string;
  email: string | null;
  phone: string | null;
  countryCode: string | null;
  financial?: { creditLimitMinor: number | null; billingCurrencyCode: string | null };
}

export interface CustomerList {
  items: CustomerListItem[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * Tek müşteri: liste satırının taşımadığı her şey — kimlik alanları, notlar, sahip.
 *
 * `api/src/modules/customers/customers.service.ts`teki `toDto` çıktısının aynısı. `financial`
 * yalnız `customer:read_financial` tutan aktöre geliyor; uç göndermediyse burada da yok.
 */
/** Müşteri satış özeti (03.11.2026): `GET /customers/:id/sales-summary`. Tutarlar minor birim. */
export interface CustomerSalesSummary {
  customerId: string;
  quotes: { total: number; accepted: number; acceptanceRateBps: number | null; open: number; draft: number; rejected: number; expired: number; cancelled: number };
  successfulSales: number;
  firstSaleAt: string | null;
  lastSaleAt: string | null;
  /** false: finans izni yok, harcama ve sipariş tutarları null. */
  moneyVisible: boolean;
  spending: { currencyCode: string; collectedMinor: number; soldMinor: number; orderCount: number; paidOrderCount: number; averageOrderMinor: number | null }[] | null;
  quoteItems: { id: string; code: string; title: string; hizli: boolean; createdAt: string; status: string; currencyCode: string; totalMinor: number | null }[];
  orderItems: {
    id: string;
    code: string;
    createdAt: string;
    status: string;
    currencyCode: string;
    revenueMinor: number | null;
    collectedMinor: number | null;
    paymentState: 'UNPAID' | 'RECORDED' | 'PARTIAL' | 'PAID' | 'PARTIALLY_REFUNDED' | 'REFUNDED';
  }[];
}

export interface CustomerDetail {
  id: string;
  type: string;
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  taxNumber: string | null;
  nationalId: string | null;
  passportNumber?: string | null;
  displayName: string;
  email: string | null;
  phone: string | null;
  countryCode: string | null;
  billingAddress?: string | null;
  languageCode: string | null;
  statusCode: string;
  ownerId: string | null;
  owner?: { id: string; displayName: string };
  tagCodes?: string[];
  notes?: { id: string; body: string; authorId: string; createdAt: string }[];
  version: number;
  mergedIntoId: string | null;
  mergedAt: string | null;
  createdAt: string;
  updatedAt: string;
  financial?: { creditLimitMinor: number | null; billingCurrencyCode: string | null; financeNotes: string | null };
}

/**
 * `findDuplicates`in dönüşü (`customers.service.ts`). `matches` içindeki her kayıt `toDto`dan
 * geçiyor ama `include` olmadan sorgulandığı için `owner`/`tagCodes`/`notes` bu listede yok;
 * gösterim yalnız kimliği ve iletişim alanlarını kullanıyor.
 */
export interface CustomerDuplicateProbe {
  normalized: { email: string | null; phone: string | null };
  matches: CustomerDetail[];
}

/**
 * GeziPuan defteri (`api/src/modules/finance/gezipuan.service.ts` `ledger()`in aynısı).
 *
 * `earnedAt` krediye deftere **yazıldığı** an, `maturesOn` harcanabilir hâle geldiği an: ikisi
 * ayrı alan çünkü kural kazanıldığında yazılır (kod: `Written at registration, with the full
 * amount: the tape is the record of the liability`), harcanabilirlik ayrı bir olay.
 */
export interface GezipuanCredit {
  creditId: string;
  earnKind: string;
  rewardId: string | null;
  incentiveReason: string | null;
  orderId: string | null;
  amountMinor: number;
  remainingMinor: number;
  state: string;
  tier: number | null;
  ratePercent: number | null;
  orderTotalMinor: number | null;
  orderPaidAt: string | null;
  maturesOn: string | null;
  maturedAt: string | null;
  lifetimeDays: number | null;
  earnedAt: string;
  expiresAt: string | null;
  expired: boolean;
  clawedBackAt: string | null;
  writtenByClient: string;
}

export interface GezipuanSpend {
  spendId: string;
  orderId: string;
  state: string;
  requestedMinor: number;
  heldMinor: number;
  expiresAt: string;
  confirmedAt: string | null;
  releasedAt: string | null;
  releaseReason: string | null;
  requestedByClient: string;
  settledByClient: string | null;
}

export interface GezipuanMovement {
  id: string;
  kind: string;
  amountMinor: number;
  orderId: string | null;
  creditId: string | null;
  spendId: string | null;
  note: string | null;
  byClient: string;
  occurredAt: string;
}

export interface GezipuanLedger {
  customerId: string;
  spendableMinor: number;
  expiringSoonMinor: number;
  heldMinor: number;
  pendingMinor: number;
  pendingNextMaturesOn: string | null;
  currency: 'EUR';
  asOf: string;
  credits: GezipuanCredit[];
  spends: GezipuanSpend[];
  movements: GezipuanMovement[];
}

/**
 * Kanal kimliği (`api/src/modules/marketing/channel-identity.service.ts` `toDto()`nun aynısı).
 *
 * Chatwoot bir kanaldır, kimlik değil: müşterinin kaydı burada, Chatwoot kimlikleri yalnız
 * bir kişinin elle bağladığı bilgidir (kapsam.md §1).
 */
export interface CustomerChannelIdentity {
  id: string;
  customerId: string;
  kind: 'INSTAGRAM' | 'FACEBOOK' | 'WHATSAPP' | 'EMAIL' | 'PHONE';
  handle: string;
  state: 'ACTIVE' | 'SUPERSEDED' | 'DISPUTED';
  chatwootContactId: string | null;
  chatwootInboxId: string | null;
  linkedBy: string;
  linkedAt: string;
  note: string | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
  resolutionNote: string | null;
}

export interface CustomerChannels {
  customerId: string;
  items: CustomerChannelIdentity[];
  chatwoot: { state: string; detail: string };
}

export interface ChannelLookup {
  kind: string;
  normalizedHandle: string;
  customerId: string | null;
  state: 'ACTIVE' | 'DISPUTED_OR_RETIRED' | 'UNKNOWN';
  history: CustomerChannelIdentity[];
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /**
     * Present on failures that came from MBDTravel v3. A screen branches on `code`, never on
     * `message`: the message is written for a person and changes with the language, the code
     * is the contract. `correlationId` is what a support request to the v3 team must quote.
     */
    readonly code?: string,
    readonly correlationId?: string | null,
    /** Makine uçlarının ve yeni servislerin `reason` kodu (`{ reason, detail }` gövdesi). */
    readonly reason?: string,
  ) {
    super(message);
  }
}

function csrfToken(): string {
  const match = /(?:^|;\s*)mbd_csrf=([^;]+)/.exec(document.cookie);
  return match?.[1] ? decodeURIComponent(match[1]) : '';
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = init.method ?? 'GET';
  const response = await sessionFetch(`/api/v1${path}`, {
    ...init,
    method,
    credentials: 'include',
    headers: {
      ...(init.body instanceof FormData ? {} : { 'content-type': 'application/json' }),
      ...(method === 'GET' ? {} : { 'x-csrf-token': csrfToken() }),
      ...(init.headers ?? {}),
    },
  });
  if (response.status === 204) return undefined as T;
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const failure = body as { message?: string | string[]; code?: string; correlationId?: string | null; reason?: string; detail?: string };
    // Nest's ValidationPipe returns `message` as an array of field errors; v3 failures return a
    // single string. Joining rather than picking the first keeps every rejected field visible.
    const detail = Array.isArray(failure.message) ? failure.message.join('. ') : (failure.message ?? failure.detail);
    const fallback = response.status === 409
      ? 'This record changed in another session. Refresh the current record before trying again.'
      : response.status === 403
        ? 'Your current role does not permit this action.'
        : `Request failed (${response.status})`;
    const metin = gecisHatasiMetni(failure.code, detail ?? '', failure.reason);
    throw new ApiError(response.status, metin ?? detail ?? fallback, failure.code, failure.correlationId, failure.reason);
  }
  return body as T;
}

async function download(path: string, suggestedName: string, serverFileName = false, fileNamePattern = /filename="([a-zA-Z0-9_.-]+\.pdf)"/): Promise<void> {
  const response = await sessionFetch(`/api/v1${path}`, { credentials: 'include' });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new ApiError(response.status, (body as { message?: string }).message ?? `Download failed (${response.status})`);
  }
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement('a');
  anchor.href = url;
  const supplied = serverFileName ? response.headers.get('Content-Disposition')?.match(fileNamePattern)?.[1] : undefined;
  anchor.download = supplied ?? suggestedName;
  document.body.append(anchor); anchor.click(); anchor.remove();
  URL.revokeObjectURL(url);
}

/** Authenticated binary read only. Never expose a provider URL or render arbitrary content types. */
async function visaPreviewBlob(id: string, documentId: string, signal: AbortSignal): Promise<Blob> {
  const response = await sessionFetch(`/api/v1/operations/visa-requests/${encodeURIComponent(id)}/documents/${encodeURIComponent(documentId)}/content`, { credentials: 'include', signal, redirect: 'error', cache: 'no-store' });
  if (!response.ok) throw new ApiError(response.status, 'Visa document preview unavailable');
  const mime = (response.headers.get('content-type') ?? '').split(';')[0]!.trim().toLowerCase();
  if (!['application/pdf', 'image/jpeg', 'image/png', 'image/webp'].includes(mime)) throw new ApiError(415, 'Unsupported preview type');
  const blob = await response.blob();
  if (blob.size === 0 || blob.size > 4 * 1024 * 1024) throw new ApiError(413, 'Invalid preview size');
  return new Blob([blob], { type: mime });
}

// ---------------------------------------------------------------------------
// CRM-A2 workflow types. Every amount is an integer count of minor units; the
// browser never performs money arithmetic in floating point.
// ---------------------------------------------------------------------------

export interface SalesProduct {
  id: string; name: string; itemType: string; description: string | null; supplierName: string | null;
  currencyCode: string; unitPriceMinor: number; taxRateBps: number; active: boolean;
  createdAt: string; updatedAt: string; createdById: string;
  /** Listeden gelir; oluşturan kişinin adı ve oturumdaki kullanıcının düzenleme yetkisi. */
  createdByName?: string | null; canEdit?: boolean;
  /** Türetilmiş USD karşılığı; gerçek değer `unitPriceMinor` + `currencyCode`. */
  priceUsdMinor?: number | null; fxRateMicros?: number | null; fxRateDate?: string | null;
}

export interface Opportunity {
  id: string;
  code: string;
  title: string;
  stage: string;
  version: number;
  currencyCode: string;
  expectedValueMinor: number;
  probability?: number;
  nextActionAt?: string | null;
  notes?: string | null;
  owner?: { id: string; displayName: string } | null;
  activities?: { id: string; note: string; occurredAt: string; actor?: { displayName: string } | null }[];
  customer: { id: string; displayName: string };
  quotes: { id: string; code: string; status: string }[];
}

/**
 * Fırsat/teklif ekranlarına giden kaynak talep özeti (12.09.2026, BE01). Ham müşteri mesajını
 * ve müşteri eşleşme adaylarını **taşımaz**: ikisi de `GET /sales/requests/:id` ile okunuyor,
 * her teklif ve liste cevabında çoğaltılmıyor.
 */
export interface QuoteRequestSummary {
  id: string;
  version: number;
  status: string;
  customerId: string | null;
  opportunityId: string | null;
  destination: string | null;
  hotelName: string | null;
  /** Tam ISO, geriye uyumluluk için. Ekranın input'a basacağı alan `checkInDate`. */
  checkIn: string | null;
  checkOut: string | null;
  /** `YYYY-MM-DD`. Saat dilimi taşımıyor, yani gün kaymıyor. */
  checkInDate: string | null;
  checkOutDate: string | null;
  nights: number | null;
  adultCount: number | null;
  childCount: number | null;
  childAges: number[] | null;
  budgetMinor: number | null;
  budgetCurrencyCode: string | null;
  budgetScope: 'TOTAL' | 'PER_PERSON' | 'UNKNOWN' | null;
  dateFlexibility: string | null;
  rooms: { adults: number; childrenAges: number[] }[] | null;
  boardPreference: string | null;
  transferPreference: string | null;
  nationality: string | null;
  hotelClass: string | null;
  visaType: string | null;
  visaDurationDays: number | null;
  preferredServices: string[] | null;
}

/**
 * `GET /sales/opportunities/:id` (12.09.2026, BE01). Liste ucunun taşımadığı iki şeyi taşıyor:
 * kaynak talep özeti ve bağlı tekliflerin sürüm/kabul durumu.
 *
 * Bir fırsatın **birden fazla** teklifi olabilir: `opportunityId` bilmek tek bir `quoteId`
 * bilmek değil. Teklif yoksa oluşturulur, birden fazlaysa açık seçim sunulur; rastgele
 * ilk/son teklif seçilmez.
 */
export interface OpportunityDetail {
  id: string;
  code: string;
  title: string;
  stage: string;
  customerId: string;
  customer: { id: string; displayName: string };
  ownerId: string;
  owner: { id: string; displayName: string } | null;
  currencyCode: string;
  expectedValueMinor: number;
  probability: number;
  nextActionAt: string | null;
  notes: string | null;
  lostReason: string | null;
  closedAt: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  /** Elle açılmış fırsatta `null` meşrudur ve uydurulmaz. */
  sourceRequest: QuoteRequestSummary | null;
  quotes: {
    id: string;
    code: string;
    status: string;
    currencyCode: string;
    currentVersionNo: number;
    acceptedVersionNo: number | null;
    version: number;
    createdAt: string;
    updatedAt: string;
  }[];
}

export interface Lead {
  id: string;
  code: string;
  status: string;
  source: string;
  title: string;
  version: number;
  currencyCode: string;
  expectedValueMinor: number;
  customerId: string;
  ownerId: string;
  customer?: { id: string; displayName: string };
  owner?: { id: string; displayName: string } | null;
}

export interface HotelStayInput { checkIn: string; checkOut: string; rooms: { adults: number; childrenAges: number[] }[]; nationality: string }
export interface HotelOfferSelection extends HotelStayInput { slug: string; searchId: string; offerId: string; roomKey: string; roomTypes: string[]; board: string; boardId: string; nights: number; expiresOn: string | null; queriedAt: string; unitPriceMinor: number; currencyCode: string }
export interface HotelPictureView { src: string; srcset: string; alt: string }
/** Oda seçicisi: otelin kapak karesi ve oda tipi başına bir kare (`GET /catalogue/hotels/:slug/photos`). */
export interface HotelRoomPhotos { cover: HotelPictureView | null; rooms: { name: string; photos: HotelPictureView[] }[] }

export type HotelAvailabilityResult = { available: false; reason: string } | { available: true; checkIn: string; checkOut: string; nights: number; expiresOn: string | null; freshness: 'FRESH' | 'EXPIRING' | 'EXPIRED'; searchId: string; offers: { offerId: string; board: string; boardId: string; roomTypes: string[]; total: { amountMinor: string; currency: string } }[] };

export interface QuoteItem {
  hotelSelection?: HotelOfferSelection | null;
  productSlug?: string | null;
  salesProductId?: string | null;
  id: string;
  lineNo: number;
  itemType: string;
  description: string;
  supplierName: string;
  quantity: number;
  unitPriceMinor: number;
  lineTotalMinor: number;
  currencyCode: string;
  travellerName?: string | null;
  serviceDate?: string | null;
  taxMinor?: number;
  feeMinor?: number;
  discountMinor?: number;
  governmentFeeMinor?: number;
  /** Kalem bazlı vergi (09.10.2026). Boş: sürümün kipini/oranını miras alır. */
  taxMode?: 'NONE' | 'EXCLUSIVE' | 'INCLUSIVE' | null;
  taxRateBps?: number | null;
  /** Kalem bazlı yolda sunucuda donmuş KDV tutarı. */
  vatMinor?: number | null;
}

export interface QuoteVersion {
  notes?: string | null;
  publishedAt?: string | null;
  /** true: bitiş tarihini kullanıcı girdi, revizyonda korunur; false: sistem varsayılanı ya da yok. */
  validUntilManual?: boolean;
  /** Liste ucu yalnız adresi taşır (`listQuotes`). */
  paymentLink?: { paymentUrl: string } | null;
  id: string;
  versionNo: number;
  status: string;
  pricingMode: string;
  subtotalMinor: number;
  discountMinor: number;
  taxMinor?: number;
  /** Sürüm düzeyi vergi modu (08.10.2026). Yok: eski teklif, kalem bazlı vergi. */
  taxMode?: 'NONE' | 'EXCLUSIVE' | 'INCLUSIVE' | null;
  taxRateBps?: number | null;
  feeMinor?: number;
  validUntil?: string | null;
  internalNotes?: string | null;
  customerNotes?: string | null;
  totalMinor: number;
  /**
   * FR-27: toplama uygulanan yukari yuvarlama farki (`applyRounding`).
   *
   * Uc bunu 22.08'den beri donduruyordu ve **hicbir ekran okumuyordu**: satisci 19,99 yaziyor,
   * teklif 20,00 gorunuyor ve arada ne oldugunu soyleyen bir sey yok. Kural bilincli, gorunmez
   * olmasi degil (olculdu 28.08.2026).
   */
  roundingAdjustmentMinor?: number;
  /**
   * FR-41: müşteriye giden oturumsuz bağlantının jetonu.
   *
   * Uç bunu 22.08'den beri gönderiyordu (`loadQuote` bütün sürüm alanlarını döndürüyor) ve
   * **hiçbir ekran okumuyordu** (ölçüldü 29.08.2026: `web/src/` içinde sıfır geçiş). Jeton
   * yalnız yayımlama anında yazılıyor (`workflow.service.ts:560`), yani bir taslakta yok; yokluğu
   * bir hata değil, henüz yayımlanmamış olmanın kendisi ve ekran bunu böyle söylüyor.
   */
  publicViewToken?: string | null;

  // --- 12.09.2026, seyahat bağlamı ve revizyon soyağacı (devir belgesi BE01, BE04) ----------
  /** Bu sürüm hazırlanırken ekranda olan talep. Elle açılmış teklifte `null` meşrudur. */
  sourceQuoteRequestId?: string | null;
  /** Talebin o **andaki** sürüm numarası. Talep sonradan değişse bu sürüm kendi anında kalır. */
  sourceQuoteRequestVersion?: number | null;
  /**
   * Seyahat bilgisinin dondurulmuş kopyası. `QuoteRequestSummary`nin kimlik alanları çıkarılmış
   * hâli; hiçbir maliyet, marj ya da tedarikçi alanı taşımaz.
   */
  travelSnapshot?: Omit<QuoteRequestSummary, 'id' | 'version' | 'status' | 'customerId' | 'opportunityId'> | null;
  /** Bu sürüm hangi sürümün revizyonu. `/renew` ile açılan sürümlerde `null`. */
  sourceQuoteVersionId?: string | null;

  items: QuoteItem[];
}

export interface Quote {
  id: string;
  /**
   * 12.09.2026, BE01: teklifin kaynak talebi. Geriye uyumlu ve nullable — elle oluşturulmuş
   * teklifte `null` ve uydurulmuyor. `versions[].travelSnapshot` **o anki** hâli gösterir, bu
   * alan ise talebin **bugünkü** hâlini; ikisi ayrı sorular ve bilerek ayrı duruyor.
   */
  sourceRequest?: QuoteRequestSummary | null;
  code: string;
  status: string;
  version: number;
  currentVersionNo: number;
  acceptedVersionNo: number | null;
  currencyCode: string;
  pricingMode: string;
  customer: { id: string; displayName: string };
  order: { id: string; code: string; status: string; invoice?: { state: string } | null } | null;
  versions: QuoteVersion[];
}

export interface OrderItem {
  id: string;
  lineNo: number;
  itemType: string;
  description: string;
  supplierName: string;
  quantity: number;
  unitPriceMinor: number;
  lineTotalMinor: number;
}

export interface SupplierBooking {
  id: string;
  supplierName: string;
  status: string;
  version: number;
  supplierReference: string | null;
  notes: string | null;
}

export interface Voucher {
  id: string;
  code: string;
  travellerName: string;
  supplierReference: string | null;
}

export interface ChecklistEntry {
  key: string;
  state: string;
  note?: string | null;
}

export interface FulfilmentTask {
  id: string;
  code: string;
  status: string;
  version: number;
  orderId: string;
  orderItemId: string;
  blockedReason: string | null;
  failureReason: string | null;
  dueAt?: string | null;
  priority?: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  notes?: { id: string; body: string; createdAt: string; author?: { displayName: string; role: string } }[];
  documents?: TaskDocument[];
  assignedTo?: { id: string; displayName: string } | null;
  requiredDetails: {
    travellerName?: string | null; serviceDate?: string | null; operationalNote?: string | null;
    packageIdentity?: { packageKey: string; catalogueCode: string; originalName: string; quotedName: string; notes: string | null };
  };
  checklist: ChecklistEntry[];
  bookings: SupplierBooking[];
  voucher: Voucher | null;
  orderItem?: OrderItem;
  order?: { id: string; code: string; status: string };
}

export interface TaskDocument {
  id: string;
  /**
   * Belgenin türü (`DocumentAsset.kind`): elle yüklenen tedarikçi teyidi ile üretilen vize
   * belgesini ayıran tek alan. Uç bu alanı zaten saklıyordu, görev yanıtına taşınması gerekti:
   * "Vize Belgeleri" görünümü listeyi başka hiçbir şeye bakarak süzemez, dosya adına bakmak ise
   * bir kural değil bir tahmindir.
   */
  kind: string;
  fileName: string;
  contentType: string;
  byteSize: number;
  sha256: string;
  uploadedAt: string;
}

/**
 * Vize destek belgesi üretimi (`api/src/modules/operations/vize-belge.dto.ts`).
 *
 * Listeler uçtaki `IsIn` kümelerinin kopyası. Kopya olduğu için tek kaynağı ucun kendisi:
 * bir anahtar orada değişirse burada da değişmesi gerekiyor ve uç yine 400 ile kendi geçerli
 * listesini yazıyor. Ekran uydurma bir anahtar göndermesin diye burada duruyorlar.
 */
export const VIZE_BELGE_TURLERI = ['otel', 'ucak', 'otel_ucak'] as const;
export type VizeBelgeTuru = (typeof VIZE_BELGE_TURLERI)[number];

export const VIZE_HAZIR_OTELLER = [
  'rove_downtown',
  'nh_collection',
  'citymax_albarsha',
  'rixos_premium',
  'atlantis_royal',
  'millennium_place',
] as const;

export const VIZE_UCUS_ROTALARI = ['dubai', 'dubai_gece', 'sarjah'] as const;

/** Üretilen belgenin `DocumentAsset.kind` değeri; görünümün süzgeci bu ikisi. */
export const VIZE_BELGE_KINDLERI = ['VIZE_OTEL_VOUCHER', 'VIZE_UCAK_BILETI'] as const;

/** Otel kataloğu arama satırı (`GET /operations/vize-belge/oteller?q=`), yalnız yayındaki oteller. */
export interface VizeOtelOzeti {
  otelNo: string | null;
  slug: string;
  ad: string;
  sehir: string | null;
  ulke: string | null;
  yildiz: number | null;
  /** Dolu ise altı hazır otelden biri (istek `hazirOtel` ile gider); yoksa katalog oteli. */
  hazirOtel?: string | null;
}

/** Seçilen otelin belgeye akacak kaydı. `eksik` boş olmayan alan için ekran "bilgi eksik" der. */
export interface VizeOtelKaydi extends VizeOtelOzeti {
  adres: string | null;
  gps: string | null;
  fotoUrl: string | null;
  telefon: null;
  eksik: Array<'adres' | 'koordinat' | 'fotograf' | 'telefon'>;
}

export interface VizeBelgeIstegi {
  belge: VizeBelgeTuru;
  yolcular?: string;
  kisiSayisi?: string;
  giris?: string;
  gece?: number;
  oda?: number;
  hazirOtel?: string;
  /** MBD otel kataloğundan seçilen otelin slug değeri; ad, adres, koordinat ve fotoğraf sunucuda katalogdan okunur. */
  katalogOtel?: string;
  otelAdi?: string;
  otelSehir?: string;
  otelAdres?: string;
  otelTelefon?: string;
  otelGps?: string;
  odaTipi?: string;
  pansiyon?: string;
  rota?: string;
  ucusTipi?: string;
  gidisTarih?: string;
  donusTarih?: string;
  koltuk?: string;
  not?: string;
}

/**
 * Siparişe bağlı olmayan bir belge (10.09.2026).
 *
 * `orderId` ve `customerId` **null olabilir** ve bu bir eksik değil, kaydın kendisi: WhatsApp'tan
 * gelen bir talepte CRM'de henüz sipariş yok, bazen müşteri kaydı da yok.
 */
export interface SerbestBelge {
  id: string;
  orderId: string | null;
  customerId: string | null;
  kind: string;
  fileName: string;
  contentType: string;
  byteSize: number;
  sha256: string;
  note: string | null;
  uploadedAt: string;
}

export interface VizeBelgeSonucu {
  documentId: string;
  kind: string;
  fileName: string;
  byteSize: number;
  sha256: string;
}

/** Üst alanlar ilk belge; `belgeler` üretilenlerin tamamı (otel_ucak için otel ve uçak). */
export interface VizeBelgeUretimSonucu extends VizeBelgeSonucu {
  belgeler: VizeBelgeSonucu[];
}

export interface IntegrationReadiness {
  providers: { key: string; authority: string; state: string; missing: string[]; localFallback: string; liveCallsPermitted: boolean }[];
  summary: { total: number; configured: number; notConfigured: number };
  note: string;
}

export interface ManagerExceptions {
  generatedAt: string;
  overdue: ManagerExceptionItem[];
  blocked: ManagerExceptionItem[];
  paymentGap: ManagerExceptionItem[];
  reconciliationVariance: ManagerExceptionItem[];
  lowMargin: ManagerExceptionItem[];
}
export interface ManagerExceptionItem { code?: string; status?: string; order?: { id: string; code: string } }

export interface Reconciliation {
  id: string;
  status: string;
  version: number;
  revenueMinor: number;
  costMinor: number;
  grossProfitMinor: number;
  marginBps: number;
  verifiedPaidMinor: number;
  refundedMinor: number;
  outstandingMinor: number;
  blockedReason: string | null;
}

export interface Order {
  id: string;
  code: string;
  status: string;
  version: number;
  currencyCode: string;
  revenueTotalMinor: number;
  customer: { id: string; displayName: string };
  items: OrderItem[];
  quoteVersion: { versionNo: number; pricingMode: string };
  tasks: FulfilmentTask[];
  reconciliation: Reconciliation | null;
}

export interface OrderListItem {
  id: string;
  code: string;
  status: string;
  customer: { id: string; displayName: string };
  items: { id: string; lineNo: number; description: string }[];
  tasks: { id: string; status: string }[];
}

export interface PaymentEvidence {
  id: string;
  kind: string;
  status: string;
  version: number;
  amountMinor: number;
  currencyCode: string;
  method: string;
  reference: string;
  rejectedReason: string | null;
  /** Returned by the finance summary; optional for older read-only consumers. */
  recordedById?: string;
}

export interface CostEntry {
  id: string;
  category: string;
  supplierName: string;
  description: string;
  amountMinor: number;
  currencyCode: string;
  orderItemId?: string | null;
}

export interface RefundRecord {
  id: string;
  status: string;
  version: number;
  amountMinor: number;
  currencyCode: string;
  reason: string;
}

/**
 * Faturanın **tek bir** koşulu ve o koşulun bugün sağlanıp sağlanmadığı
 * (`api/src/modules/finance/invoice.readiness.ts`, `InvoiceRequirement`in aynısı).
 *
 * `detail` ve `source` ekrana **çizilmiyor**: ikisi de sunucunun kendi diliyle yazılmış
 * (alan adları, dosya yolları, uç adresleri) ve bu deponun ekran kuralı geliştirici dilini
 * yasaklıyor (`tests/e2e/onyuz-kalite.spec.ts`). Ekran `key` üzerinden kendi Türkçe cümlesini
 * yazıyor. Alanlar yine de tipte duruyor çünkü uç onları gönderiyor ve bir gün bir tanı
 * ekranı onları okuyabilir; okunmayan bir alanı tipten silmek, gelen veriyi yok saymak olurdu.
 */
export interface InvoiceRequirement {
  key: string;
  met: boolean;
  detail: string;
  source: string;
}

export interface FinanceSummary {
  orderId: string;
  currencyCode: string;
  revenueMinor: number;
  costMinor: number;
  grossProfitMinor: number;
  marginBps: number;
  verifiedPaidMinor: number;
  recordedUnverifiedMinor: number;
  refundedMinor: number;
  outstandingMinor: number;
  /** Server-computed customer liability, never inferred from the displayed balance. */
  overpaidMinor?: number;
  display: { revenue: string; cost: string; grossProfit: string; outstanding: string; margin: string };
  payments: PaymentEvidence[];
  costs: CostEntry[];
  refunds: RefundRecord[];
  /** Vizede iade yok (04.10.2026). Eski sunucu bu alanı göndermeyebilir. */
  refundPolicy?: { hasVisa: boolean; allVisa: boolean; visaLineMinor: number; refundableMinor: number };
  reconciliation: Reconciliation | null;
  invoice: {
    /** `NOT_CONFIGURED` | `BLOCKED` | `READY` | `ISSUED`. Kesme çağrısı yalnız `READY` iken geçer. */
    state: string;
    provider: string;
    note: string;
    /** `POST /finance/orders/{id}/invoice`in kesme öncesi hangi alanları beklediği (anahtar adı). */
    missing: string[];
    /** Sağlayıcı NOT_CONFIGURED ise eksik ortam değişkeni adları (değer değil). */
    missingConfig: string[];
    /**
     * Sipariş koşulları (ödemenin tamamı, maliyetin belli olması, siparişin açık olması) ve
     * fatura sağlayıcısının istediği veri alanları. İkisi 22.08'den beri gönderiliyordu ve
     * hiçbir ekran okumuyordu: Finans yalnız eksiklerin **anahtar listesini** görüyordu, hangi
     * koşulun sağlandığını değil. Sıra da buradan okunuyor — kur dondurulmadan fatura
     * kesilemiyor ve bunu söyleyen alan `parabirimi` anahtarının `met` değeri.
     */
    preconditions: InvoiceRequirement[];
    data: InvoiceRequirement[];
  };
}

/**
 * `POST /finance/orders/:id/invoice-fx` cevabı.
 *
 * `order` uçtan Prisma satırı olarak olduğu gibi geliyor; ekranın okuduğu üç alan burada. Kur
 * bir kez donuyor: aynı kuru tekrar göndermek `idempotentReplay: true` döner, **farklı** bir
 * kur göndermek 409 `INVOICE_FX_ALREADY_FROZEN` ile reddedilir ve ekran o reddi olduğu gibi
 * yazar — sessiz bir üzerine yazma yok.
 */
export interface InvoiceFxResult {
  idempotentReplay: boolean;
  /** Faturaya yazılacak tutar, alt birim, fatura para biriminde. Hiçbir yerde ondalıklı sayı değil. */
  invoiceAmountMinor: number;
  order: {
    id: string;
    version: number;
    currencyCode: string;
    invoiceFxRateMicros: number | null;
    invoiceFxSource: string | null;
    invoiceFxDate: string | null;
  };
}

export interface Oversight {
  order: Order;
  timeline: {
    entityType: string;
    entityId: string;
    from: string;
    to: string;
    reason: string | null;
    actor: { id: string; displayName: string; role: string } | null;
    occurredAt: string;
  }[];
  bottlenecks: { taskId: string; code: string; status: string; ageHours: number; reason: string; impliesSuccess: boolean }[];
  auditTrail: { action: string; outcome: string; targetType: string; targetId: string; actorRole: string; occurredAt: string }[];
  financial: {
    revenueMinor: number;
    costMinor: number;
    grossProfitMinor: number;
    marginBps: number;
    outstandingMinor: number;
    currencyCode: string;
    display: { revenue: string; cost: string; grossProfit: string; outstanding: string; margin: string };
  } | null;
}

export interface QuoteItemInput {
  hotelSelection?: HotelOfferSelection;
  itemType: string;
  description: string;
  supplierName: string;
  /** v3 katalog slug'ı; kalem ürün ağacından geldiyse dolu, elle yazıldıysa yok. */
  productSlug?: string;
  /** CRM ürününden gelen kalem; "Özel paket" bununla tanınıyor (04.10.2026). */
  salesProductId?: string;
  quantity: number;
  unitPriceMinor: number;
  travellerName?: string;
  serviceDate?: string;
  taxMinor?: number;
  feeMinor?: number;
  discountMinor?: number;
  /** Yalnız VISA: satır tutarının içindeki devlet harcı (satır toplamı). KDV matrahından düşer. */
  governmentFeeMinor?: number;
  taxMode?: 'NONE' | 'EXCLUSIVE' | 'INCLUSIVE';
  taxRateBps?: number;
}

// --- teklif asistanı: talep alma (satis-akisi-vaka.md adım 2-3) ------------

/**
 * Bir alanın kaynağı ve güveni (`QuoteRequest.fieldMeta`, `prisma/schema.prisma`).
 *
 * FR-04: `lowConfidence` **uçtan geliyor**, tarayıcı eşik hesabı yapmıyor. Sağlayıcı eşiği
 * kendi uygulayıp `true`/`false` yazıyor (`request-parsing.provider.ts`), ekran yalnız çiziyor;
 * ikinci bir eşik, bir gün ikisinin ayrışması demekti.
 */
export interface QuoteRequestFieldMeta {
  source: 'PARSED' | 'MANUAL' | 'RULE';
  confidence: number;
  lowConfidence: boolean;
}

/**
 * Ayrıştırmanın dört hâli (`QuoteRequestParsingStatus`). İkisi arıza (`NOT_CONFIGURED`,
 * `FAILED`), biri normal bir sonuç (`PARSED_EMPTY`: müşteri gerçekten bir şey yazmamış), biri
 * başarı. Ekranda dördü ayrı görünmek zorunda: kaynak kusur (ölçüldü 27.08.2026,
 * `api/test/integration/talep-ayristirma-hata-gorunurlugu.spec.ts`) dördünün de aynı boş forma
 * benzemesiydi.
 */
export type QuoteRequestParsingStatus = 'NOT_CONFIGURED' | 'FAILED' | 'PARSED' | 'PARSED_EMPTY';

/**
 * `POST /sales/requests` ve kardeşlerinin döndürdüğü satırın aynısı.
 *
 * Uç `QuoteRequest` satırını **olduğu gibi** gönderiyor (`request-intake.service.ts`, ayrı bir
 * DTO dönüşümü yok), o yüzden tarih alanları ISO dize olarak geliyor ve `childAges`/
 * `preferredServices` JSON dizi.
 *
 * `hotelName` kozmetik bir alan değil: fiyat sorgusunu tetikleyen eşik **onun** üstünde
 * (`price-trigger-eligibility.ts`, 29.08.2026). Otel adı boşsa CRM v3 katalogunu hiç okumuyor ve
 * fiyat kutusu boş kalıyor; model o alanı dolduramadığında kutuyu çalıştıran şey satışçının onu
 * yazması oluyor.
 *
 * Ekran alan listesini yine de `fieldMeta`nın anahtarlarından okuyor, bu tipten değil: uca yeni
 * bir ayrıştırma alanı eklendiğinde ekran onu çizmeye devam ediyor, burası bir gün geç kalsa bile.
 */
export interface QuoteRequest {
  id: string;
  status: string;
  rawText: string;
  destination: string | null;
  hotelName: string | null;
  checkIn: string | null;
  checkOut: string | null;
  nights: number | null;
  adultCount: number | null;
  childCount: number | null;
  childAges: number[] | null;
  budgetMinor: number | null;
  budgetCurrencyCode: string | null;
  hotelClass: string | null;
  /**
   * Tatilin karakteri ("balayı", "aile tatili") ve şehrin içinde istenen bölge ("JBR").
   * Ürün Sahibi, 21.09.2026: ikisi de müşteriden alınması gereken bilgiler listesinde. Bu iki
   * alan açılmadan önce ikisi de `preferredServices` içinde düz metin olarak duruyordu.
   */
  holidayConcept: string | null;
  preferredRegion: string | null;
  visaType: string | null;
  /**
   * Gün sayısı, tam sayı. "1 aylık vize" diyen bir talepte bilerek `null` kalıyor: bir ay sabit
   * bir gün sayısı değil ve çevirmek uydurmak olurdu. Ekranın kutusu bu yüzden boş bırakılabilir
   * olmalı, ve dolduğunda tam sayı göndermeli — uç dizeyi 400 ile reddediyor.
   */
  visaDurationDays: number | null;
  preferredServices: string[] | null;

  // --- 12.09.2026, satış talep akışı (devir belgesi BE03) -----------------------------------
  // Altısı da uçtan geriye uyumlu geliyor: eski kayıtlarda `null`. Ekran bunları henüz
  // çizmiyor; tip önce geliyor ki çizen iş `unknown` üstünde çalışmasın.

  /** `null` = hiç konuşulmadı. `UNKNOWN` = tutar var, kapsamı bilinmiyor. İkisi ayrı hâl. */
  budgetScope: 'TOTAL' | 'PER_PERSON' | 'UNKNOWN' | null;
  /** Müşterinin kendi kelimeleriyle, ör. "ekim başı olur". Buradan kesin tarih türetilmez. */
  dateFlexibility: string | null;
  /** `null` "dağılım bilinmiyor" demek, "tek oda" demek değil. */
  rooms: { adults: number; childrenAges: number[] }[] | null;
  boardPreference: string | null;
  transferPreference: string | null;
  /** ISO 3166-1 alpha-2. `null` = sorulmadı; ekran `TR` varsaymamalı. */
  nationality: string | null;

  fieldMeta: Record<string, QuoteRequestFieldMeta>;
  /** Alanları **kimin** ürettiği; `parsingStatus` ise ne olduğu. İkisi aynı soru değil. */
  parsingProvider: string;
  parsingStatus: QuoteRequestParsingStatus;
  /** `null`: ayrıştırma arka planda sürüyor, alanlar henüz dolmadı. */
  parsedAt: string | null;
  parsingErrorCode: string | null;
  /** Yalnız arıza ve boş sonuçta dolu; ham sağlayıcı gövdesi değil, okunur Türkçe cümle. */
  parsingReasonText: string | null;
  parsingAttempts: number;
  customerId: string | null;
  /** FR-05: deterministik e-posta/telefon eşleşmesi, model çağrısı değil. */
  customerMatchSuggestions: { customerId: string; displayName: string; matchedOn: string }[] | null;
  newCustomerSuggested: boolean;
  opportunityId: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * `PATCH /sales/requests/:id/fields` gövdesi (`UpdateQuoteRequestDto`).
 *
 * Verilmeyen alan **dokunulmamış** sayılıyor: uç yalnız gönderilen alanları `MANUAL`/tam güvene
 * çeviriyor (`updateFields`). Bu yüzden ekran da yalnız temsilcinin düzelttiği alanları
 * gönderiyor; hepsini göndermek, ayrıştırıcının düşük güven işaretlerini tek bir kaydetmeyle
 * silerdi. Uç `forbidNonWhitelisted` ile çalışıyor (`api/src/main.ts`), yani burada olmayan bir
 * anahtar 400 döner — ekranın düzenlenebilir alan listesi bu tiple sınırlı.
 */
export interface QuoteRequestFieldPatch {
  expectedVersion: number;
  destination?: string;
  hotelName?: string;
  checkIn?: string;
  checkOut?: string;
  nights?: number;
  adultCount?: number;
  childCount?: number;
  childAges?: number[];
  budgetMinor?: number;
  budgetCurrencyCode?: string;
  hotelClass?: string;
  holidayConcept?: string;
  preferredRegion?: string;
  visaType?: string;
  visaDurationDays?: number;
  preferredServices?: string[];

  /*
   * 12.09.2026: her alan artık `null` da kabul ediyor ve `null` **temizle** demek. Alanı hiç
   * göndermemek eskisi gibi "dokunma". İkisi ayrı: bir alanı boşaltmanın yolu boş dize ya da
   * sıfır göndermek değil, `null` göndermek.
   */
  budgetScope?: 'TOTAL' | 'PER_PERSON' | 'UNKNOWN' | null;
  dateFlexibility?: string | null;
  rooms?: { adults: number; childrenAges?: number[] }[] | null;
  boardPreference?: string | null;
  transferPreference?: string | null;
  nationality?: string | null;
}

/**
 * `GET /sales/requests/:id/price-lookup`, tek sağlayıcının hâli (`toView`,
 * `price-trigger-orchestrator.service.ts`).
 *
 * Dört hâlin üçünde tutar **yok** ve nedeni uçtan okunur bir cümle olarak geliyor. Ekranın işi
 * o cümleyi çizmek; boş bir hücre ya da uydurulmuş bir rakam ikisi de aynı yalanı söylerdi.
 */
export type PriceLookupProviderView =
  | { status: 'READY'; amountMinor: number; currencyCode: string; asOf: string; productSlug: string }
  | { status: 'PENDING'; productSlug: string }
  | { status: 'FAILED'; reasonText: string; productSlug: string }
  | { status: 'SKIPPED'; reasonText: string; productSlug: string | null };

/**
 * Tedarikçiye gerçekten gönderilen girdi (12.09.2026, BE05). Hangi değerin talepten geldiği,
 * hangisinin varsayıldığı burada açık — ekran "bu fiyat hangi seyahat için" sorusuna bu
 * nesneden cevap verebilir ve varsayımı sessiz bırakmaz.
 */
export interface PriceLookupQueryInput {
  slug: string;
  checkIn: string;
  checkOut: string;
  rooms: { adults: number; childrenAges: number[] }[];
  nationality: string;
  nationalityAssumed: boolean;
  roomsAssumed: boolean;
  checkOutDerivedFromNights: boolean;
}

export interface PriceLookupView {
  paximum: PriceLookupProviderView;
  roompilot: PriceLookupProviderView;
  /**
   * 12.09.2026, BE05. `stale: true`, satırdaki tutarın **başka bir seyahate** ait olduğunu
   * söyler: talebin tarih/otel/oda/uyruk bilgisi o sorgudan beri değişmiş. Tutar hâlâ dönüyor
   * ama ekranın onu güncel fiyat gibi göstermemesi gerekiyor.
   */
  freshness: {
    paximum: { stale: boolean; queryInput: PriceLookupQueryInput | null };
    roompilot: { stale: boolean; queryInput: PriceLookupQueryInput | null };
  };
  /** Talebin şu anki fiyat girdisi; satır yoksa ya da eşik geçilmediyse `null`. */
  currentQueryInput: PriceLookupQueryInput | null;
}

/**
 * `GET /sales/item-suggestions` bir satırı (`ItemSuggestionService.suggest`).
 *
 * FR-25: `finance:read` taşımayan aktörde maliyet alanlarının hepsi boş döner
 * (`costUnavailable: true`, `netPriceMinor: null`, `costSource: null`) — uç o aktör için v3'ün
 * maliyet ucunu hiç çağırmıyor. Ekran bu yüzden hiçbir maliyet alanının **dolu olduğunu
 * varsaymıyor**; `referenceSalePriceMinor` (v3'ün genel ürün ağacındaki satış fiyatı) herkese
 * açık ve yalnız oryantasyon için.
 */
export interface ItemSuggestion {
  productSlug: string;
  itemType: string;
  description: string;
  supplierName: string;
  referenceSalePriceMinor: number | null;
  referenceCurrencyCode: string;
  availabilityVerified: boolean;
  priceCapturedAt: string;
  costUnavailable: boolean;
  netPriceMinor: number | null;
  netCurrencyCode: string | null;
  costUnavailableReason: string | null;
  costSource: string | null;
  costFreshness: string | null;
  costAsOf: string | null;
  costMayBeStale: boolean;
}

/**
 * Fiyat önerisi (`GET /sales/price-recommendation`, `POST .../refresh`), satışçı görünümü.
 *
 * Uç FR-25 disiplinini kendi tarafında uyguluyor: `finance:read` taşımayan aktöre bu şekli
 * gönderiyor (`toSalesView`, `sales-intake/price-recommendation.service.ts`). 27.08.2026
 * kararıyla (Ürün Sahibi, 21.08 kararının geri alınışı) tedarikçi maliyeti (`amount`,
 * `currency`) satışçıya da dönüyor — fiyatı savunabilsin diye. Kapalı kalan: kaynak, tazelik
 * ayrıntısı ve marj kuralı; bu tip onları hiç taşımıyor çünkü bu ekran hiçbir zaman onlara
 * erişmiyor.
 */
export interface PriceRecommendationView {
  product: { slug: string; itemType: string; refreshable: boolean };
  supplierCost:
    | { known: true; amount: number; currency: string; stale: boolean; reasonText: string | null }
    | { known: false; reasonText: string };
  /**
   * Booking piyasa fiyatı (RoomPilot üzerinden). 29.08.2026'da bağlandı; bu tip o güne kadar
   * `known: false` diye çiviliydi ve fiyat ekrana **hiç çıkamıyordu**.
   *
   * `pending` bir başarısızlık değil: soğuk bir Booking sorgusu yaklaşık yarım dakika sürüyor ve
   * v3 onu bilerek arka plana atıyor. Ekran bunu bekleyiş olarak çizip tekrar soruyor.
   */
  marketReference:
    | { known: true; amountMinor: string | number; currency: string; asOf: string }
    | { known: false; pending: boolean; reasonText: string };
  recommendation:
    | {
        available: true;
        suggestedUnitPriceMinor: number;
        currencyCode: string;
        stale: boolean;
        /**
         * Sayının **hangi yoldan** geldiği. Satışçı bir fiyatı savunacaksa bunu bilmeli:
         * `MARKET_SPREAD` piyasadan türetilmiş, `MARKET_SPREAD_BELOW_COST` aynı formül ama sonuç
         * alışımızın altında (o otelde rekabetçi değiliz, ve bu bilgi gizlenmiyor),
         * `MARGIN_RULE` piyasa fiyatı gelmediği için marj kuralı.
         */
        basis: 'MARKET_SPREAD' | 'MARKET_SPREAD_BELOW_COST' | 'MARGIN_RULE';
      }
    | { available: false; reasonText: string };
}

/** `POST .../price-recommendation/refresh`in oda gövdesi; `V3ProductCostRefreshRequest` ile birebir aynı şekil. */
export interface PriceRecommendationRoomInput {
  adults: number;
  childrenAges?: number[];
}

/**
 * MamoPay ödeme bağlantısı (`POST /sales/quotes/:id/versions/:no/payment-link`).
 *
 * TRY hâlâ reddediliyor — uç bu durumda 409 dönüyor, `ApiError.code` `TRY_COLLECTION_FORBIDDEN`
 * taşıyor ve `message` zaten okunur bir Türkçe cümle (`payment-link.service.ts`). Karma sepet
 * (otel + otel dışı kalem karışık) artık reddedilmiyor: Ürün Sahibi kararı (25.08.2026) doğrudan
 * tahsil, `holdAndChargeLater` bu durumda `false` döner. Yalnız `basketComposition ===
 * 'HOTEL_ONLY'` provizyonda kalır (`holdAndChargeLater: true`).
 */
export interface PaymentLinkResult {
  id: string;
  quoteVersionId: string;
  provider: string;
  providerLinkId: string;
  paymentUrl: string;
  amountMinor: number;
  currencyCode: string;
  basketComposition: 'HOTEL_ONLY' | 'NON_HOTEL_ONLY' | 'MIXED';
  holdAndChargeLater: boolean;
  /** `true`: var olan bağlantı döndü, sağlayıcıya ikinci kez gidilmedi. */
  alreadyExisted: boolean;
}

/**
 * WhatsApp gönderim katmanının cevabı (`whatsapp-send.service.ts`).
 *
 * Ayrık birleşim, çünkü sunucu **başarıyı ve reddi aynı şekle sokmuyor**: `ok: true` bir
 * sağlayıcı mesaj kimliği taşır, `ok: false` reddin adını (`state`) ve gerekçesini taşır. İki
 * durumu tek bir isteğe bağlı alan yığınına indirmek, ekranda "gerekçesiz bir başarısızlık" ya
 * da "sağlayıcısız bir başarı" çizmeyi mümkün kılardı; tip bunu baştan imkânsız kılıyor.
 *
 * `logId` her iki durumda da var: her deneme bir `WhatsAppMessageLog` satırı yazıyor ve
 * operatörün destek isteğinde vereceği numara bu.
 */
export type WhatsAppSendResult =
  | { ok: true; logId: string; state: 'SENT'; provider: string; providerMessageId: string }
  | {
      ok: false;
      logId: string;
      /** `NOT_CONFIGURED` | `BLOCKED_CONSENT` | `BLOCKED_WINDOW` | `BLOCKED_MEDIA` | `FAILED` */
      state: string;
      reason: string;
    };

/**
 * Proformanın WhatsApp mesajına eklenip eklenmediği ve **eklenmediyse niçin**
 * (`quote-whatsapp.service.ts`, `ProformaEki`).
 */
export interface QuoteWhatsAppProforma {
  attached: boolean;
  url: string | null;
  bytes: number | null;
  reason: string | null;
}

/**
 * Satış akışı vakası madde 9'un tek çağrısı
 * (`POST /sales/quotes/:id/versions/:no/whatsapp/payment-link`).
 *
 * Uç 201 dönüyor ama bu "gitti" demek değil: istek işlendi, **sonucu gövdede**. `gonderim.ok`
 * false ise mesaj gitmedi ve reddin adı orada duruyor. Ödeme bağlantısı yine de üretilmiş
 * olur (sürüme bağlı ve kalıcı), bu yüzden `odemeBaglantisi` her iki durumda da dolu.
 */
export interface QuoteWhatsAppResult {
  quoteId: string;
  versionNo: number;
  toPhone: string;
  odemeBaglantisi: {
    paymentUrl: string;
    /** Tam sayı, minör birim. Ekranda yalnız `para()` ile, hiçbir yerde ondalıklı sayı olarak değil. */
    amountMinor: number;
    currencyCode: string;
    holdAndChargeLater: boolean;
    alreadyExisted: boolean;
  };
  proforma: QuoteWhatsAppProforma;
  gonderim: WhatsAppSendResult;
}

/**
 * Satış ekranından seçilebilen gönderim amaçları.
 *
 * `whatsapp-template.dto.ts`teki `WHATSAPP_TEMPLATE_PURPOSES` beş değer taşıyor; buradaki üçü
 * vakanın **satışçıya ait** adımları (madde 6 otel/oda fotoğrafı, madde 7 aktiviteler PDF'i,
 * madde 9 teklif ve ödeme bağlantısı). `MARKETING_DISCOUNT` madde 15'te pazarlamanın,
 * `SUPPLIER_NOTIFICATION` madde 17'de operasyonun işi. İkisi de bu ekrandan seçilebilseydi
 * satışçıya kendi rolünde olmayan iki kapı açılırdı. Uç yine tam listeyi kabul ediyor; daraltma
 * ekranın kendi kararı, ikinci bir yetki mekanizması değil.
 */
export const WHATSAPP_AMACLARI = ['HOTEL_ROOM_PHOTOS', 'ACTIVITIES_PDF', 'QUOTE_PAYMENT_LINK'] as const;
export type WhatsAppAmaci = (typeof WHATSAPP_AMACLARI)[number];

/**
 * Müşteriye giden oturumsuz proforma adresi (`GET /public/quotes/:token` ve `.../pdf`).
 *
 * **Neden `window.location.origin`:** bu istemcinin bütün çağrıları göreli (`/api/v1/...`,
 * `request()` ve `download()` içinde), yani panel hangi kökenden servis ediliyorsa uç da o
 * kökenden servis ediliyor: geliştirmede Vite'ın `/api` vekili, üretimde panelin önündeki
 * sunucu. Dolayısıyla panelin kendi kökeni, tarayıcının **ölçebildiği** tek doğru köken.
 *
 * Sunucunun kendi `PUBLIC_QUOTE_BASE_URL` değişkeni (`public-quote-link.ts`) müşteriye
 * yazılacak kökeni ayrıca tanımlıyor ve panele hiçbir uçtan gelmiyor. İkisi ayrıştığında
 * yetkili olan sunucudur: WhatsApp cevabındaki `proforma.url` sunucunun ürettiği mutlak adres
 * ve ekran onu aldığı anda buradakinin yerine onu gösteriyor (`Workflow.tsx`).
 *
 * Jeton yoksa adres **uydurulmuyor**: `null` dönüyor ve çağıran taraf sebebi yazıyor.
 */
export function publicQuoteLinks(token: string | null | undefined): { viewUrl: string; pdfUrl: string } | null {
  const temiz = (token ?? '').trim();
  if (temiz === '') return null;
  const viewUrl = `${window.location.origin}/api/v1/public/quotes/${encodeURIComponent(temiz)}`;
  return { viewUrl, pdfUrl: `${viewUrl}/pdf` };
}

export interface BelgeArsivSorgu { page?: number; pageSize?: number; q?: string; kind?: string; linked?: 'yes' | 'no'; state?: 'available' | 'purged' }
export interface BelgeArsivSatiri {
  id: string; fileName: string; kind: string; contentType: string; byteSize: number; uploadedAt: string;
  orderId: string | null; orderCode: string | null; webOrderReference: string | null; customerName: string | null;
  linked: boolean; fileAvailable: boolean; purgedAt: string | null; purgeAt: string | null; customerVisible: boolean;
  sourceDocumentId: string | null; versionNo: number | null;
}
export type Paged<T> = { items: T[]; total: number; page: number; pageSize: number };

// ---------------------------------------------------------------------------
// MBDTravel v3 — the Commerce authority. These types mirror the CRM API, which mirrors v3's
// wire contract. Nothing is renamed on the way through: a field that means one thing in v3
// and reads as another in the CRM is a bug waiting for the first person who compares them.
// ---------------------------------------------------------------------------

export interface Money {
  amount: number;
  currency: string;
}

/** Provenance attached by the CRM to every v3-backed response. */
export interface V3Source {
  system: string;
  baseUrl: string;
  correlationId: string;
  fetchedAt: string;
  elapsedMs: number;
  attempts: number;
}

export interface OffsetPage {
  limit: number;
  offset: number;
  total: number;
}

export interface SalesReportParams {
  language: string;
  currency: string;
  from: string;
  to: string;
  groupBy?: string;
  limit?: number;
  offset?: number;
}

export interface SalesRow {
  productId: string;
  slug: string;
  name: string | null;
  departureId: string | null;
  departureOn: string | null;
  orderCount: number;
  unitCount: number;
  revenue: Money;
  refundedOrderCount: number;
  refundedUnitCount: number;
  refunded: Money;
}

export interface SalesReport {
  source: V3Source;
  window: { from: string; to: string; groupBy: string };
  totals: {
    orderCount: number;
    unitCount: number;
    revenue: Money;
    refundedOrderCount: number;
    refundedUnitCount: number;
    refunded: Money;
  };
  rows: SalesRow[];
  page: OffsetPage;
}

export interface V3Fulfilment {
  autoBook: boolean | null;
  releasedAt: string | null;
  awaitingRelease: boolean;
  sagaStatus: string | null;
  saleCode: string | null;
}

export interface V3OrderSummary {
  reference: string;
  status: string;
  total: Money;
  createdAt: string;
  paidAt: string | null;
  passengerCount: number;
  supplierCodes: string[];
  fulfilment: V3Fulfilment;
}

export interface V3OrderList {
  source: V3Source;
  orders: V3OrderSummary[];
  page: OffsetPage;
}

export interface V3OrderDetail {
  source: V3Source;
  order: {
    reference: string;
    status: string;
    total: Money;
    createdAt: string;
    paidAt: string | null;
    supplierCodes: string[];
    items: {
      productId: string;
      slug: string;
      name: string | null;
      departureId: string | null;
      departureOn: string | null;
      slot: string;
      quantity: number;
      unitPrice: Money;
    }[];
    passengers: { position: number; firstName: string; lastName: string; passengerType: string; isMaster: boolean }[];
  };
  fulfilment: V3Fulfilment & { steps: { step: string; position: number; status: string; errorCode: string | null }[] };
}

export interface ReleaseQueueDeparture {
  departureId: string | null;
  departureOn: string | null;
  productId: string;
  productName: string | null;
  supplierCode: string;
  orderCount: number;
  passengerCount: number;
  orders: { reference: string; createdAt: string; passengerCount: number; total: Money }[];
}

export interface ReleaseQueue {
  source: V3Source;
  departures: ReleaseQueueDeparture[];
  page: { limit: number; total: number };
  shortfall: { complete: boolean; missingOrders: number };
  release: { available: boolean; reason: string };
}

export interface Receivable {
  reference: string;
  orderStatus: string;
  orderTotal: Money;
  paidAt: string | null;
  paymentStatus: string | null;
  invoice: {
    status: string;
    number?: string | null;
    externalId?: string | null;
    amount?: Money | null;
    issuedAt?: string | null;
    attemptCount?: number;
    failureReason?: string | null;
  };
  fx: { rateDate: string; eurUsdScaled: number; scale: number; source: string };
}

export interface Receivables {
  source: V3Source;
  receivables: Receivable[];
  page: OffsetPage;
}

export interface CatalogueParams {
  language: string;
  currency: string;
  kind?: string;
  sort?: string;
  limit?: number;
  offset?: number;
}

export interface VisaVariants {
  currency: string;
  variants: { code: string; durationDays: number; entries: string; price: { adult: number; child: number } }[];
}

export interface Product {
  sourceProductSlug?: string;

  slug: string;
  kind: string;
  name: string;
  description: string | null;
  publishedAt: string | null;
  fromPrice: Money | null;
  airline: string | null;
}

export interface ProductList {
  source: V3Source;
  products: Product[];
  page: OffsetPage;
}

export interface DepartureList {
  source: V3Source;
  departures: { id: string; departureOn: string; prices: { slot: string; amount: number; currency: string }[] }[];
}

// --- CRM-A13: tedarikçiler ve kullanıcı yönetimi --------------------------

export interface Supplier {
  id: string;
  code: string;
  name: string;
  kind: string;
  status: string;
  /** MANUAL: siparişleri bir insan işliyor. Operasyonun günü buna göre değişiyor. */
  workMode: string;
  requiresIpAllowlist: boolean;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  website: string | null;
  notes: string | null;
  openQuestions?: number;
}

export interface SupplierList {
  items: Supplier[];
  total: number;
  page: number;
  pageSize: number;
}

export interface SupplierQuestion {
  id: string;
  supplierId: string;
  supplierCode: string;
  supplierName: string;
  question: string;
  status: string;
  askedAt: string;
  ownerName: string;
  ownerIsActive: boolean;
  answer: string | null;
}

export interface CrmUser {
  id: string;
  email: string;
  displayName: string;
  role: string;
  isActive: boolean;
  lastLoginAt: string | null;
  /** Açık oturum sayısı (yalnız liste ucunda). */
  openSessions?: number;
  /** Bekleyen parola bağlantısı: INVITE hiç girmemiş hesap, RESET sıfırlama. Yoksa null. */
  setupPending?: 'INVITE' | 'RESET' | null;
}

export interface PasswordSetupLink {
  setupUrl: string;
  expiresAt: string;
  purpose: 'INVITE' | 'RESET';
}

export interface CrmUserList {
  items: CrmUser[];
  total: number;
  page: number;
  pageSize: number;
}

// ---------------------------------------------------------------------------
// İK: çalışan dizini (Ürün Sahibi, 30.08.2026).
//
// `CrmUser` ile aynı satırlara bakan ama aynı olmayan bir tip, ve bu kasıtlı: `CrmUser`
// yönetim ekranının şekli (`/users`), bu ise dizinin şekli (`/hr/directory`). İkisini tek tipe
// bağlamak, bir gün yönetim ucuna eklenen bir alanı İK ekranına da sızdırırdı.
//
// Ne birinde ne ötekinde `passwordHash` var; uç onu hiç göndermiyor.
// ---------------------------------------------------------------------------

export interface IkDizinSatiri {
  id: string;
  displayName: string;
  email: string;
  role: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface IkDizinOzeti {
  total: number;
  active: number;
  inactive: number;
  neverLoggedIn: number;
  byRole: { role: string; count: number }[];
}

export interface IkDizin {
  generatedAt: string;
  sort: string;
  direction: string;
  shown: number;
  /** Uç tavana dayandı: ekran bunu sessizce yutmuyor, yazıyor. */
  truncated: boolean;
  summary: IkDizinOzeti;
  items: IkDizinSatiri[];
}

// ---------------------------------------------------------------------------
// CRM-A14 dashboard (docs/tasarim/dashboard.md).
//
// Ekranın tek vaadi: bekleyen ve yaşlanan iş. Bu tiplerde tek bir toplam, tek bir grafik
// serisi, tek bir hedef yok — çünkü hiçbirinin bugün girdisi yok.
// ---------------------------------------------------------------------------

export interface AgeStats {
  count: number;
  /** Kuyruk başına yaş medyanı, saat. **Boş kuyrukta null**, sıfır değil. */
  medianAgeHours: number | null;
  oldestAgeHours: number | null;
  lateCount: number;
  buckets: Record<string, number>;
  /** false ise yaş hesaplanamıyor ve ekran bunu yazar; sahte sayaç konmaz. */
  measured: boolean;
}

export interface DashboardQueue {
  key: string;
  label: string;
  anchor: string;
  ages: AgeStats;
  scanLimited: boolean;
  shown: number;
  items: Record<string, unknown>[];
  notes: string[];
}

export interface DashboardBox {
  key: string;
  label: string;
  lists: DashboardQueue[];
  notes: string[];
}

export interface DashboardSection {
  key: string;
  label: string;
  owns: string;
  badgeLate: number;
  lateThresholdHours: number;
  bucketLabels: Record<string, string>;
  band1: DashboardBox[];
  band2: DashboardBox[];
  band3: { key: string; label: string; detail: string }[];
  blocker?: { key: string; state: string; label: string; detail: string; dismissible: false };
  generatedAt: string;
}

export interface DashboardSectionIndex {
  generatedAt: string;
  lateThresholdHours: number;
  sections: { key: string; label: string; owns: string; badgeLate: number }[];
}

/**
 * CRM-B6: tedarikçi iptali kuyruğu (`api/src/modules/operations/supplier-cancellation.service.ts`).
 * Ürün Sahibi, 21.08.2026: "GlobDMC iptalleri direkt yansımasın... biz operasyonda görelim,
 * müşteri aranıp farklı turla değiştirilebilir." Kayıt açılınca hiçbir şey değişmiyor;
 * GeziPuan yalnız `CANCELLED` kararında geri alınıyor, `SUBSTITUTED`de dokunulmuyor.
 */
export interface SupplierCancellation {
  id: string;
  orderReference: string;
  customerId: string;
  supplierCode: string;
  productId: string | null;
  departureId: string | null;
  departureOn: string | null;
  reason: string;
  state: 'OPEN' | 'SUBSTITUTED' | 'CANCELLED';
  reportedAt: string;
  reportedBy: string;
  decidedAt: string | null;
  decidedBy: string | null;
  decisionNote: string | null;
  replacementOrderReference: string | null;
  gezipuan: { clawedBackMinor: number; shortfallMinor: number; currency: 'EUR' } | null;
}

export interface SupplierCancellationList {
  items: SupplierCancellation[];
  total: number;
  page: number;
  pageSize: number;
  state: string;
}

/** CRM-B7: vize talebi kuyruğu (`api/src/modules/operations/visa.service.ts`). */
export type VisaDocumentKind = 'PASSPORT' | 'BIOMETRIC' | 'HOTEL_BOOKING' | 'FLIGHT_TICKET' | 'VISA_PDF' | 'INSURANCE_POLICY';
export interface VisaDocumentMetadata { id?: string; kind: VisaDocumentKind; uploaded: boolean; byteSize?: number; uploadedAt?: string }
export interface VisaDocumentList { status: 'SUBMITTED' | 'IN_PROGRESS' | 'APPROVED' | 'REJECTED' | 'CANCELLED'; documents: VisaDocumentMetadata[] }

export interface VisaRequest {
  id: string;
  orderReference: string;
  itemReference: string;
  customerId: string;
  productCode: string;
  productLabel: string;
  applicantType: 'ADULT' | 'CHILD';
  applicantName: string;
  state: 'RECEIVED' | 'SUBMITTED' | 'ISSUED' | 'REJECTED';
  receivedAt: string;
  submittedAt: string | null;
  submittedBy: string | null;
  supplierReference: string | null;
  issuedAt: string | null;
  rejectedAt: string | null;
  decisionNote: string | null;
  decidedBy: string | null;
  listPrice: { currency: string; amountMinor: number } | null;
  /**
   * Yalnız `finance:read` tutan aktöre geliyor (CRM-A15); uç göndermediyse burada `null`.
   * Alan hiç yoksa (`byId` çağrılmadıysa, yalnız listedeyse) `undefined`.
   */
  cost?: {
    currency: string;
    expectedMinor: number;
    committedMinor: number;
    realizedMinor: number;
    varianceMinor: number | null;
    entries: { id: string; state: string; category: string; description: string; amountMinor: number; currency: string; recordedAt: string; recordedByClient: string }[];
  } | null;
}

export interface VisaRequestList {
  items: VisaRequest[];
  total: number;
  page: number;
  pageSize: number;
  state: string;
}

/** Operasyon "Transfer" turu: `api/src/modules/operations/transfer.service.ts` (09.10.2026). */
export type TransferJobState = 'PENDING' | 'DRIVER_ASSIGNED' | 'COMPLETED' | 'CANCELLED';
export interface TransferJob {
  id: string;
  orderReference: string;
  itemReference: string;
  leg: 'OUTBOUND' | 'RETURN';
  unitNo: number;
  state: TransferJobState;
  passengerName: string;
  phone: string | null;
  airportCode: string | null;
  flightNumber: string | null;
  pickupAt: string | null;
  /** Istanbul yerel tarih ve saat, sunucu hesaplar. */
  pickupDate: string | null;
  pickupTime: string | null;
  pickupPoint: string | null;
  dropoffPoint: string | null;
  side: 'EUROPE' | 'ANATOLIA' | null;
  vehicleLabel: string | null;
  passengerCount: number | null;
  luggageCount: number | null;
  customerNote: string | null;
  sale: { amountMinor: number; currency: string } | null;
  currency: string;
  detailMissing: boolean;
  driverName: string | null;
  vehiclePlate: string | null;
  opsNote: string | null;
  assignedAt: string | null;
  assignedBy: string | null;
  completedAt: string | null;
  completedBy: string | null;
  cancelledAt: string | null;
  openedAt: string;
}
export interface TransferJobList {
  items: TransferJob[];
  total: number;
  page: number;
  pageSize: number;
  state: string;
  counts: Record<TransferJobState, number>;
}
export interface TransferJobQuery {
  state?: string; from?: string; to?: string; side?: string; airport?: string; leg?: string;
  q?: string; sort?: string; page?: number; pageSize?: number;
}

/** CRM-B8: `operations/service-completions` — GeziPuan'ı bitiş tarihi olmayan üründe olgunlaştırır. */
export interface ServiceCompletionResult {
  matured: boolean;
  reason?: 'NO_CREDIT' | 'ALREADY_MATURED' | 'CLAWED_BACK' | 'COMPLETION_BEFORE_PAYMENT';
  creditId: string | null;
  customerId: string | null;
  amountMinor: number;
  expiresAt: string | null;
}

/**
 * Denetim maddesi B5: `webshop/orders`, `webshop/customers`, `webshop/support-cases`.
 *
 * Yukarıdaki "MBDTravel v3" bölümünden farklı bir şey okuyor. Oradaki uçlar v3'e her seferinde
 * yeniden soruyor ve hiçbir kopya tutmuyor; buradakiler tam tersi — v3'ün CRM'e `musteri/kayitlar`,
 * `satis/web-siparisleri` ve `destek/talepler` uçlarıyla bildirdiği ve `WebshopService`in CRM'in
 * kendi veritabanına yazdığı asıl kayıt (`api/src/modules/webshop/webshop.module.ts`,
 * `WebshopReadController`, izin `web_intake:read`). Bu yüzden `source`/`Provenance` alanı yok:
 * v3'e bağlanılmıyor, CRM kendi defterini okuyor.
 */
export interface WebOrderLineRecord {
  title?: string | null;
  productKind?: string | null;
  itemReference: string;
  productId: string;
  departureId: string;
  departureOn: string;
  quantity: number;
  unitAmountMinor: number;
}

export interface WebFulfilmentTaskRecord {
  orderReference: string;
  itemReference: string;
  state: string;
  productId: string;
  departureOn: string;
  quantity: number;
  openedAt: string;
  completedAt: string | null;
}

export interface WebOrderRecord {
  reference: string;
  state: string;
  currencyCode: string;
  totalMinor: number;
  customerEmail: string;
  customerName: string;
  customerExternalId: string | null;
  placedAt: string;
  paidAt: string | null;
}

/** Web siparişi belgesinin müşteri portalına gönderimi (`V3DocumentPush`). */
export interface WebOrderDocumentPush {
  action: 'PUBLISH' | 'WITHDRAW';
  state: 'PENDING' | 'UPLOADED' | 'DONE' | 'FAILED';
  /** Sunucunun ürettiği tek satırlık durum; ekran bunu olduğu gibi gösteriyor. */
  label: string;
  attempts: number;
  lastErrorCode: string | null;
  lastError: string | null;
  lastAttemptAt: string | null;
  nextAttemptAt: string;
  deliveredAt: string | null;
  deliveredSha256: string | null;
}

export interface WebOrderDocument {
  id: string;
  kind: string;
  fileName: string;
  contentType: string;
  byteSize: number;
  sha256: string;
  uploadedAt: string;
  webOrderItemReference: string | null;
  customerVisible: boolean;
  /** Silindi ama müşteriden geri çekilmesi henüz bitmedi. */
  deleted: boolean;
  v3Push: WebOrderDocumentPush | null;
}

export interface WebOrderDocumentList {
  orderReference: string;
  items: WebOrderDocument[];
  total: number;
  v3Push: { enabled: boolean; configured: boolean; reason: string | null };
}

export interface WebOrderInvoiceRecord {
  companyName: string;
  taxOffice: string;
  taxNumber: string;
  invoiceAddress: string;
}

export interface WebOrderDetailRecord extends WebOrderRecord {
  /** Müşterinin özel talep notu (v3 göç 0377). Yazmadıysa `null`. */
  customerRequest: string | null;
  /** Kurumsal fatura (v3 göç 0365). Kutu işaretlenmediyse `null`. */
  invoice: WebOrderInvoiceRecord | null;
  lines: WebOrderLineRecord[];
  tasks: WebFulfilmentTaskRecord[];
}

export interface WebSupportCaseRecord {
  reference: string;
  email: string;
  displayName: string;
  subject: string;
  orderReference: string | null;
  state: string;
  raisedAt: string;
}

export interface WebCustomerRecord {
  externalId: string;
  email: string;
  displayName: string;
  languageCode: string | null;
  registeredAt: string;
  lastSeenAt: string;
  /** 27.08.2026: bağlandığı ana kayıt. `null` ise henüz kimse hangi müşteri olduğuna karar vermedi. */
  customer?: { id: string; displayName: string; email: string | null; statusCode: string } | null;
}

/** Site kaydının bağlandığı ana kayıt ya da makinenin işaret ettiği aday. */
export interface WebCustomerMasterRef {
  id: string;
  displayName: string;
  email: string | null;
}

/**
 * Karar bekleyen bir site kaydı (Ürün Sahibi kararı, 27.08.2026).
 *
 * `suggestions` okuma anında yeniden hesaplanıyor: satıra yazılmış "ana kayıt yok" cümlesi,
 * temsilci ana kaydı açar açmaz yanlış olur.
 */
export interface WebCustomerReviewRecord {
  externalId: string;
  email: string;
  displayName: string;
  registeredAt: string;
  customerId: string | null;
  customer: WebCustomerMasterRef | null;
  linkMatch: 'EMAIL' | 'MANUAL' | null;
  linkedAt: string | null;
  linkedBy: string | null;
  reviewState: 'NONE' | 'OPEN' | 'RESOLVED';
  reviewReason: 'NO_MASTER_RECORD' | 'NAME_MISMATCH' | 'NAME_CANDIDATE' | null;
  reviewDetail: string | null;
  reviewCandidate: WebCustomerMasterRef | null;
  reviewResolvedAt: string | null;
  reviewResolvedBy: { id: string; displayName: string } | null;
  reviewResolutionNote: string | null;
  suggestions: {
    emailMatch: WebCustomerMasterRef | null;
    nameCandidates: { id: string; displayName: string }[];
    nameScanTruncated: boolean;
  };
}

/**
 * Ön talep: tedarikçi turu kestiğinde rezervasyonun yerine geçen üç alanlı formun kaydı.
 * Bir **bekleme listesi**: ürün tekrar açıldığında bu kişilere haber gidecek.
 */
export interface WebDemandRecord {
  reference: string;
  /** Yalniz WhatsApp teklifinde (`WHATSAPP_TEKLIF`) bos: musteri numarasini WhatsApp'tan getiriyor. */
  phone: string | null;
  partySize: number;
  travelOn: string;
  productSlug: string;
  productName: string;
  /**
   * Talebin niye acildigi. On bir deger, alti kaynaktan: turun kalkis takvimi, aktivitenin urun
   * duzeyinde kapanmasi, capa sayfalari, aktivitenin dolan seanslari, turun eksik cocuk ya da
   * bebek fiyati ve WhatsApp kisa teklifi. Kanonik liste `WebDemandState`
   * (`prisma/schema.prisma`); buradaki birlesim onun elle tutulan aynasi ve
   * `metin/commerce.ts` icindeki `sebep`/`sebepHamle` her degeri karsilamak zorunda -
   * karsilamayan bir deger satis ekranina bos hucre olarak dusuyordu.
   */
  state:
    | 'TAKVIM_KAPALI'
    | 'TEDARIKCI_CEKTI'
    | 'SEASONAL_CLOSURE'
    | 'SUPPLIER_MAINTENANCE'
    | 'NOT_YET_SOURCED'
    | 'CAPA_KATALOGDA_YOK'
    | 'CAPA_EYLEM_UYUSMAZLIGI'
    | 'CAPA_URUN_YOK'
    | 'KAPASITE_DOLU'
    | 'FIYAT_EKSIK'
    | 'WHATSAPP_TEKLIF';
  languageCode: string | null;
  raisedAt: string;
  followUpState: 'WAITING' | 'CONTACTED' | 'CLOSED';
  followUpNote: string | null;
  followUpAt: string | null;
  followUpBy: { id: string; displayName: string } | null;
  customerId: string | null;
  customer: { id: string; displayName: string; phone: string | null } | null;
  /**
   * Fiyat talebi alanlari (`FIYAT_EKSIK`, 30.09.2026). Bekleme listesi satirlarinda hepsi bos;
   * fiyat talebinde de gelmemis olabilirler, ekran bosu "gelmedi" diye yaziyor.
   */
  departureId: string | null;
  adults: number | null;
  children: number | null;
  infants: number | null;
  contactName: string | null;
  contactEmail: string | null;
  /**
   * Alt birimde. `FIYAT_EKSIK`te yalniz yetiskinlerin tutari, `WHATSAPP_TEKLIF`te musterinin
   * gordugu toplamin tamami.
   */
  pricedTotalMinor: number | null;
  pricedCurrency: string | null;
}

export interface WebDemandList {
  items: WebDemandRecord[];
  total: number;
  page: number;
  pageSize: number;
  /** Rozet sayısı, sayfalamadan bağımsız. */
  waitingTotal: number;
}

export interface WebCustomerReviewList {
  items: WebCustomerReviewRecord[];
  total: number;
  page: number;
  pageSize: number;
  /** Rozet sayısı. Sayfalamadan etkilenmiyor: bir birikimi sayfaya bölmek onu olduğundan az gösterir. */
  openTotal: number;
}

/**
 * FR-20: bir marj kuralı (`prisma/schema.prisma`, `MarginRule`; uç satırı olduğu gibi döndürüyor).
 *
 * Fiyat önerisinin dayandığı politika bu tabloda duruyor ve bugüne kadar panelden **hiç**
 * yönetilemiyordu: veritabanındaki tek kuralı tohum yazmıştı. Boş bırakılan her alan bir joker —
 * `itemType` yoksa kural her kalem türüne, `destination` yoksa her destinasyona uyuyor. En düşük
 * `priority` kazanıyor.
 *
 * `marginBps` ve `marginFixedMinor` iç veridir (FR-25) ve bu uç zaten `margin_rule:write`
 * arkasında; ekran yine de para ve marj sütunlarını `finance:read` olmadan çizmiyor.
 */
export interface MarginRule {
  id: string;
  name: string;
  priority: number;
  active: boolean;
  version: number;
  destination: string | null;
  itemType: string | null;
  seasonStartMonth: number | null;
  seasonEndMonth: number | null;
  customerSegmentTag: string | null;
  minAmountMinor: number | null;
  maxAmountMinor: number | null;
  bandCurrencyCode: string | null;
  /** Baz puan: 1850 = %18,50. Tarayıcı bu sayıyı hiçbir zaman ondalıklı olarak taşımıyor. */
  marginBps: number | null;
  marginFixedMinor: number | null;
  decidedBy: string;
  decidedAt: string;
  createdAt: string;
}

/**
 * NFR-04: bir istem şablonu sürümü (`PromptTemplate`).
 *
 * Bir anahtar için en fazla bir sürüm aktif olabilir. Aktif sürüm yoksa uç modele hiç gitmiyor
 * ve talep ayrıştırma yedek yola düşüyor — yani boş bir tablo bir arıza değil, **bir durum**, ve
 * o durumu düzeltmenin panelde bir yolu olmalı.
 */
export interface PromptTemplate {
  id: string;
  key: string;
  version: number;
  active: boolean;
  body: string;
  changeNote: string | null;
  createdById: string;
  createdAt: string;
}

/** Uçtaki `PROMPT_TEMPLATE_KEYS` ile birebir aynı liste (`common/ai/prompt-template.dto.ts`). */
export const ISTEM_SABLONU_ANAHTARLARI = ['REQUEST_PARSING', 'QUOTE_NARRATIVE'] as const;
export type IstemSablonuAnahtari = (typeof ISTEM_SABLONU_ANAHTARLARI)[number];

function search(params: Readonly<Record<string, string | number | undefined>> | object): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') query.set(key, String(value));
  }
  return query.toString();
}

export interface UserNotification { id: string; kind: string; customerId: string | null; customer: { displayName: string } | null; webDemandId: string | null; waitingHours: number | null; createdAt: string; readAt: string | null }
export interface UserNotifications { items: UserNotification[]; unreadCount: number }

export interface ConversationInbox { id: string; name: string; channel: string; address: string | null }
export interface ConversationItem {
  channel?: string;
  id: string; status: string; inboxId: string | null; subject?: string | null;
  contact: { id: string | null; name: string; email: string | null; phone: string | null };
  customer: { id: string; displayName: string } | null;
  assignee: { id: string | null; name: string } | null;
  unreadCount: number; updatedAt: unknown; matchReason?: string; matchExcerpt?: string;
}
export interface ConversationDetail extends ConversationItem {
  candidates: { id: string; displayName: string }[];
  hasOlder: boolean;
  messages: { id: string; content: string; private: boolean; type: unknown; createdAt: unknown; sender: string; status: string;
    attachments: { id: string; name: string; url: string | null }[] }[];
}

// --- Web sitesi bölümü (taslak A, 07.10.2026): `webshop/panel/...` okuma yüzü ----------------
export type WsTur = 'destek' | 'on' | 'vize' | 'sip';
export type WsSahip = 'sende' | 'musteri' | 'kapali';
export interface WsEslesme {
  alan: string;
  aralik?: [number, number];
}
export interface WsIs {
  key: string;
  tur: WsTur;
  reference: string;
  shortReference: string;
  who: string;
  memberExternalId: string | null;
  what: string;
  state: string;
  ageDays: number;
  raisedAt: string;
  owner: WsSahip;
  next: string;
  threshold: number;
  overdue: boolean;
  amountMinor: number | null;
  currencyCode: string | null;
  eslesme?: WsEslesme[];
  /** Yalnız destek mesajında (17.10.2026): kaynak ve sınıf. */
  destek?: WsDestekOzeti;
  /** Yalnız ön talepte: ne telefon ne e-posta var (19.10.2026). */
  iletisimYok?: boolean;
}
export type WsDestekSinifi = 'GERCEK' | 'SUPHELI' | 'SPAM';
export type WsDestekAkisi = 'form-ziyaretci' | 'form-uye' | 'hayal-rotasi' | 'tatil-bulucu' | 'bilet-ctg' | 'esik-bildirimi' | 'periyodik-denetim' | 'sistem' | 'bilinmeyen';
export type WsDestekKategorisi = 'siparis' | 'vize' | 'odeme' | 'iade' | 'isletme' | 'grup' | 'schengen' | 'ingiltere' | 'abd' | 'rusya' | 'cin' | 'degisiklik' | 'iptal' | 'diger';
export interface WsDestekOzeti {
  akis: WsDestekAkisi;
  kategori: WsDestekKategorisi | null;
  dil: 'tr' | 'en';
  sinif: WsDestekSinifi;
  puan: number;
  kurallar: { kod: string; puan: number; neden: string }[];
  subject: string;
}
export interface WsDestekCekmecesi extends WsDestekOzeti {
  email: string;
  body: string;
  orderReference: string | null;
  decidedBy: string;
  system: boolean;
  customer: { id: string; name: string } | null;
  member: { externalId: string; name: string } | null;
  spamMark: 'SPAM' | 'DEGIL' | null;
}
export interface WsKutu {
  today: number;
  last7: number;
  previous7: number;
  daily: number[];
}
export interface WsAsama {
  id: WsTur;
  title: string;
  sub: string;
  rows: { label: string; count: number; highlight: boolean; key: string }[];
  note: string;
  warn: boolean;
  crm: string | null;
}
export interface WsGorulme {
  externalId: string;
  name: string;
  daysAgo: number | null;
  registeredDaysAgo: number;
  emailVerified: boolean;
}
export interface WsOverview {
  measuredAt: string;
  thresholds: { SUPPORT: number; DEMAND: number; VISA: number; ORDER: number };
  truncated: boolean;
  visaIncluded: boolean;
  boxes: {
    uye: WsKutu & { lastRegistrationDaysAgo: number | null };
    destek: WsKutu;
    onVize: WsKutu;
    siparis: WsKutu & { paid: number };
    donen: { known: number; total: number; seen7: number; seen30: number; never: number; squares: { n: number; name: string; daysAgo: number | null }[] };
  };
  tabs: { requests: number; members: number; orders: number };
  summary: { open: number; mine: number; customer: number; overdue: number; pendingPayment: number };
  items: WsIs[];
  oldest: WsIs[];
  stages: WsAsama[];
  funnel: { label: string; short: string; count: number }[];
  orderHero: { paid: number; total: number; cancelled: number; pending: number; sentence: string };
  risk: {
    seen: WsGorulme[];
    seenTotal: number;
    never: WsGorulme[];
    neverTotal: number;
    unknown: number;
    total: number;
    counters: { code: string; count: number; label: string; memberExternalId: string | null }[];
  };
  health: {
    hiddenSystemMessages: number;
    support: { real: number; system: number; spam: number };
    hiddenSpam: number;
    orders: { v3: number | null; crm: number };
    visa: { crm: number; v3: number | null };
    lastSeen: { delivered: number; total: number };
    consent: { filled: number; total: number };
  };
}
export interface WsSayfa {
  total: number;
  page: number;
  pageSize: number;
  truncated: boolean;
}
export interface WsTalepler extends WsSayfa {
  items: WsIs[];
  counts: {
    tabs: Record<WsSahip, number>;
    types: { hepsi: number; destek: number; on: number; vize: number; onvize: number; sistem: number; spam: number };
    suspicious: number;
    overdue: number;
    open: number;
  };
  thresholds: { SUPPORT: number; DEMAND: number; VISA: number; ORDER: number };
  visaIncluded: boolean;
  hiddenSystemMessages: number;
  hiddenSpam: number;
}
export interface WsUyeSatiri {
  externalId: string;
  name: string;
  email: string;
  hasPhone: boolean;
  tier: number | null;
  registeredAt: string;
  daysSinceSeen: number | null;
  sessionsKnown: boolean;
  neverLoggedIn: boolean;
  emailVerified: boolean;
  orderCount: number;
  newsletter: string | null;
  master: { id: string; name: string | null } | null;
  reviewState: string;
  eslesme?: WsEslesme[];
}
export interface WsUyeler extends WsSayfa {
  items: WsUyeSatiri[];
  counts: { hepsi: number; dogrulanmamis: number; dogrulanmis: number; oturumsuz: number; siparisli: number; 'kayip-odemeli': number; total: number };
  lastSeenKnown: number;
  consentFilled: number;
}
export interface WsSiparisSatiri {
  reference: string;
  shortReference: string;
  state: string;
  amountMinor: number;
  currencyCode: string;
  who: string | null;
  member: { externalId: string; name: string; guest: boolean } | null;
  ageDays: number;
  placedAt: string;
  waiting: boolean;
  overdue: boolean;
  eslesme?: WsEslesme[];
}
export interface WsSiparisler extends WsSayfa {
  items: WsSiparisSatiri[];
  counts: { hepsi: number; bekleyen: number; geciken: number; odendi: number; iptal: number };
  totalAll: number;
  currencies: { code: string; count: number }[];
  v3Count: number | null;
  threshold: number;
}
export interface WsOlay {
  type: 'uye' | 'oturum' | 'sip' | 'destek' | 'on' | 'vize';
  at: string | null;
  text: string;
  tone: '' | 'open' | 'gone';
  ref?: string;
}
export interface WsKisi {
  member: {
    externalId: string;
    name: string;
    email: string;
    phone: string | null;
    tier: number | null;
    registeredAt: string;
    firstLoginAt: string | null;
    lastLoginAt: string | null;
    daysSinceSeen: number | null;
    sessionCount: number | null;
    sessionsKnown: boolean;
    emailVerifiedAt: string | null;
    membershipConsentedAt: string | null;
    newsletter: string | null;
    master: { id: string; name: string | null } | null;
    reviewState: string;
    reviewReason: string | null;
  };
  orders: { count: number; pending: number; paid: number; cancelled: number };
  events: WsOlay[];
  measuredAt: string;
}
export interface WsCekmece {
  tur: WsTur;
  reference: string;
  who: string;
  isMember: boolean;
  memberExternalId: string | null;
  facts: [string, string][];
  events: WsOlay[];
  state: string | null;
  next: string | null;
  owner: WsSahip | null;
  ageDays: number | null;
  /** Yalnız destek mesajında. */
  support: WsDestekCekmecesi | null;
  /** Yalnız ön talepte (19.10.2026): kime nasıl ulaşılır ve teklifin okunan alanları. */
  onTalep?: WsOnTalepCekmecesi | null;
}
export interface WsOnTalepCekmecesi {
  whatsappTeklif: boolean;
  productName: string;
  phone: string | null;
  /** `wa.me` için artısız rakamlar; kayıtlı numara E.164 değilse `null`. */
  waRakamlar: string | null;
  telNumara: string | null;
  iletisimYok: boolean;
  aramaKodu: string | null;
  partySize: number | null;
  adults: number | null;
  children: number | null;
  infants: number | null;
  travelOn: string | null;
  pricedTotalMinor: number | null;
  pricedCurrency: string | null;
}
export interface WsSorgu {
  q?: string;
  from?: string;
  to?: string;
  tur?: string;
  tab?: string;
  over?: string;
  sort?: string;
  f?: string;
  who?: string;
  page?: number;
  pageSize?: number;
}


// ---------------------------------------------------------------------------
// Operasyon / Tur PDF Duzenleme (29.10.2026)
// ---------------------------------------------------------------------------

export type TurPdfBelgeTuru = 'TUR' | 'OTEL' | 'UCUS' | 'DIGER';
export interface TurPdfKural { ara: string; yeni: string }
export interface TurPdfProfil {
  id: string; name: string; documentType: TurPdfBelgeTuru; supplier: { id: string; name: string } | null;
  rules: TurPdfKural[];
  attachment: { documentId: string; fileName: string; byteSize: number; pages: number | null } | null;
  /** Ekin KAYNAK sayfasi (ek PDF icinden). */
  attachmentPage: number;
  /** Ciktidaki SIRA (sonuc PDF'inde). Kaynak sayfadan ayri alan. */
  insertAt: number;
  /** Sayfa basi cikti ust siniri (KB, 1 KB = 1024 bayt): cikti en cok `cikti sayfa sayisi x bu deger`. */
  maxKbPerPage: number;
  version: number; updatedAt: string;
}
export interface TurPdfKaynak { id: string; fileName: string; byteSize: number; orderId: string | null; customerId: string | null; uploadedAt: string; versionCount: number; sourceAvailable: boolean }
export interface TurPdfSurum {
  id: string; fileName: string; byteSize: number; sourceDocumentId: string | null; versionNo: number | null;
  profileId: string | null; profileVersion: number | null; uploadedAt: string;
}
export interface TurPdfIslemSonucu { blob: Blob; fileName: string; matches: number; pages: number; outputBytes: number; originalBytes: number; compressionStep: number }
export interface TurPdfEkOptimizasyonu { originalBytes: number; outputBytes: number; compressionStep: number; maxBytes: number | null; pages: number }
export interface TurPdfSikistirma { step: number; originalBytes: number; outputBytes: number; maxBytes: number | null }

/** Content-Disposition'dan adi okur: once RFC 5987 `filename*` (Turkce harf, bosluk), yoksa `filename`. */
export function dosyaAdiBasliktan(baslik: string | null): string | undefined {
  if (!baslik) return undefined;
  const utf = baslik.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (utf) { try { return decodeURIComponent(utf); } catch { /* ASCII adina dus */ } }
  return baslik.match(/filename="([^"]+)"/)?.[1];
}

async function turPdfIsle(path: string, body: object, signal?: AbortSignal): Promise<TurPdfIslemSonucu> {
  const response = await sessionFetch(`/api/v1/operations/tur-pdf${path}`, {
    method: 'POST', credentials: 'include', signal,
    headers: { 'content-type': 'application/json', 'x-csrf-token': csrfToken() },
    body: JSON.stringify(body),
  });
  // Yalniz 200 + application/pdf basaridir. JSON hata govdesi hicbir kosulda PDF diye ele alinmaz.
  if (!response.ok || !(response.headers.get('content-type') ?? '').startsWith('application/pdf')) {
    const failure = (await response.json().catch(() => ({}))) as { message?: string | string[]; code?: string };
    const detail = Array.isArray(failure.message) ? failure.message.join('. ') : failure.message;
    throw new ApiError(response.ok ? 502 : response.status, detail ?? `Request failed (${response.status})`, failure.code);
  }
  const name = dosyaAdiBasliktan(response.headers.get('Content-Disposition')) ?? 'belge.pdf';
  return { blob: await response.blob(), fileName: name, matches: Number(response.headers.get('x-pdf-matches') ?? 0), pages: Number(response.headers.get('x-pdf-pages') ?? 0), outputBytes: Number(response.headers.get('x-pdf-output-bytes') ?? 0), originalBytes: Number(response.headers.get('x-pdf-original-bytes') ?? 0), compressionStep: Number(response.headers.get('x-pdf-compression-step') ?? 0) };
}

export const api = {
  turPdfProfiller: (documentType?: TurPdfBelgeTuru) =>
    request<{ items: TurPdfProfil[]; total: number }>(`/operations/tur-pdf/profiller${documentType ? `?documentType=${documentType}` : ''}`),
  turPdfProfilYarat: (body: { name: string; documentType: TurPdfBelgeTuru; supplierId?: string; rules: TurPdfKural[]; attachmentPage?: number; insertAt?: number; maxKbPerPage?: number }) =>
    request<TurPdfProfil>('/operations/tur-pdf/profiller', { method: 'POST', body: JSON.stringify(body) }),
  turPdfProfilGuncelle: (id: string, body: { expectedVersion: number; name?: string; documentType?: TurPdfBelgeTuru; supplierId?: string; rules?: TurPdfKural[]; attachmentPage?: number; insertAt?: number; maxKbPerPage?: number }) =>
    request<TurPdfProfil>(`/operations/tur-pdf/profiller/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(body) }),
  turPdfEkYukle: (id: string, body: { expectedVersion: number; fileName: string; contentBase64: string; attachmentPage?: number }) =>
    request<TurPdfProfil & { optimization: TurPdfEkOptimizasyonu }>(`/operations/tur-pdf/profiller/${encodeURIComponent(id)}/ek`, { method: 'PUT', body: JSON.stringify(body) }),
  turPdfEkKaldir: (id: string, expectedVersion: number) =>
    request<{ removed: boolean; profile: TurPdfProfil }>(`/operations/tur-pdf/profiller/${encodeURIComponent(id)}/ek?expectedVersion=${expectedVersion}`, { method: 'DELETE' }),
  turPdfEkIndir: (id: string, fileName: string) => download(`/operations/tur-pdf/profiller/${encodeURIComponent(id)}/ek/icerik`, fileName),
  turPdfKaynaklar: (q?: string, page = 1) =>
    request<{ items: TurPdfKaynak[]; total: number; page: number; pageSize: number }>(`/operations/tur-pdf/belgeler?page=${page}${q ? `&q=${encodeURIComponent(q)}` : ''}`),
  turPdfKaynakYukle: (body: { fileName: string; contentBase64: string }, signal?: AbortSignal) =>
    request<{ id: string }>('/operations/tur-pdf/belgeler', { method: 'POST', body: JSON.stringify(body), signal }),
  turPdfSurumler: (documentId: string) =>
    request<{ source: { id: string; fileName: string }; items: TurPdfSurum[]; total: number }>(`/operations/tur-pdf/belgeler/${encodeURIComponent(documentId)}/surumler`),
  turPdfBelgeIndir: (documentId: string, fileName: string) => download(`/operations/tur-pdf/belgeler/${encodeURIComponent(documentId)}/icerik`, fileName),
  turPdfIsle: (body: { documentId: string; profileId: string; profileVersion: number; password?: string }, signal?: AbortSignal) => turPdfIsle('/islem', body, signal),
  turPdfKaydet: (body: { documentId: string; profileId: string; profileVersion: number; password?: string; processKey: string }) =>
    request<{ reused: boolean; version: TurPdfSurum; matches?: number; pages?: number; compression?: TurPdfSikistirma }>('/operations/tur-pdf/islem/kaydet', { method: 'POST', body: JSON.stringify(body) }),
  seoAi: () => request<SeoAi>('/marketing/seo/ai'),
  seoPosts: () => request<SeoPosts>('/marketing/seo/posts'),
  seoSearch: () => request<SeoSearch>('/marketing/seo/search'),
  seoCosts: () => request<SeoCosts>('/marketing/seo/costs'),
  seoAddQuestion: (body: { text: string; label?: string; branded?: boolean }) => request<{ id: string }>('/marketing/seo/ai/questions', { method: 'POST', body: JSON.stringify(body) }),
  seoPatchQuestion: (id: string, body: { active: boolean }) => request<{ id: string }>(`/marketing/seo/ai/questions/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  seoMainTargets: () => request<SeoMainTargets>('/marketing/seo/main-targets'),
  setSeoTargetUrlStatus: (id: string, status: SeoTargetUrlStatus) => request<{ id: string }>(`/marketing/seo/main-targets/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  seoKpis: () => request<SeoKpis>('/marketing/seo/kpis'),
  seoKeywords: (params: SeoKeywordQuery = {}) => request<SeoKeywords>(`/marketing/seo/keywords?${search(params)}`),
  seoIndex: () => request<SeoIndex>('/marketing/seo/index'),
  seoLinks: () => request<SeoLinks>('/marketing/seo/links'),
  seoPlan: () => request<SeoPlan>('/marketing/seo/plan'),
  seoSetTarget: (kpi: string, day: 30 | 60 | 90, body: SeoTargetInput) => request<{ id: string }>(`/marketing/seo/targets/${encodeURIComponent(kpi)}/${day}`, { method: 'PUT', body: JSON.stringify(body) }),
  seoAddKeyword: (body: SeoKeywordInput) => request<{ id: string }>('/marketing/seo/keywords', { method: 'POST', body: JSON.stringify(body) }),
  seoKeywordSummary: () => request<SeoKeywordSummary>('/marketing/seo/keywords/summary'),
  seoBulkKeywordTarget: (body: SeoKeywordEdit & { ids: string[] }) => request<{ updated: number }>('/marketing/seo/keywords/bulk-target', { method: 'POST', body: JSON.stringify(body) }),
  seoPatchKeyword: (id: string, body: SeoKeywordEdit & { active?: boolean }) => request<{ id: string }>(`/marketing/seo/keywords/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  seoSetLinks: (body: { referringDomains: number; qualityDomains?: number }) => request<{ id: string }>('/marketing/seo/manual/links', { method: 'PUT', body: JSON.stringify(body) }),
  seoSetReviews: (body: { platform: SeoReviewPlatform; reviewCount: number; rating?: number }) => request<{ id: string }>('/marketing/seo/manual/reviews', { method: 'PUT', body: JSON.stringify(body) }),
  seoDecide: (id: string, decision: string) => request<{ id: string }>(`/marketing/seo/decisions/${encodeURIComponent(id)}`, { method: 'POST', body: JSON.stringify({ decision }) }),
  conversations: (page = 1, customerId?: string, filters: { channel?: string; inboxId?: string; q?: string; scope?: string; status?: string; queue?: string; agentId?: string } = {}) => request<{ items: ConversationItem[]; page: number; hasMore: boolean }>(`/conversations?${new URLSearchParams({ page: String(page), ...(customerId ? { customerId } : {}), ...Object.fromEntries(Object.entries(filters).filter(([, value]) => !!value)) }).toString()}`),
  conversation: (id: string, before?: string) => request<ConversationDetail>(`/conversations/${encodeURIComponent(id)}${before ? `?before=${encodeURIComponent(before)}` : ''}`),
  conversationAttachment: (id: string, file: File, privateNote: boolean) => { const form = new FormData(); form.set('file', file); form.set('private', String(privateNote)); return request(`/conversations/${encodeURIComponent(id)}/attachments`, { method: 'POST', body: form }); },
  readConversation: (id: string) => request(`/conversations/${encodeURIComponent(id)}/read`, { method: 'POST', body: '{}' }),
  conversationInboxes: () => request<{ items: ConversationInbox[] }>('/conversations/inboxes'),
  conversationAgents: () => request<{ items: { id: string; name: string }[] }>('/conversations/agents'),
  replyConversation: (id: string, content: string, privateNote: boolean) => request(`/conversations/${encodeURIComponent(id)}/messages`, { method: 'POST', body: JSON.stringify({ content, private: privateNote }) }),
  linkConversation: (id: string, customerId: string) => request(`/conversations/${encodeURIComponent(id)}/customer`, { method: 'POST', body: JSON.stringify({ customerId }) }),
  unlinkConversation: (id: string) => request(`/conversations/${encodeURIComponent(id)}/customer/unlink`, { method: 'POST', body: '{}' }),
  assignConversation: (id: string, agentId: string) => request(`/conversations/${encodeURIComponent(id)}/assignee`, { method: 'POST', body: JSON.stringify({ agentId }) }),
  conversationStatus: (id: string, status: 'open' | 'resolved') => request(`/conversations/${encodeURIComponent(id)}/status`, { method: 'POST', body: JSON.stringify({ status }) }),
  notifications: () => request<UserNotifications>('/notifications'),
  readNotification: (id: string) => request<{ ok: true }>(`/notifications/${encodeURIComponent(id)}/read`, { method: 'POST', body: '{}' }),
  login: (email: string, password: string) => request<{ user: Me }>('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  logout: () => request<void>('/auth/logout', { method: 'POST', body: '{}' }),
  me: () => request<Me>('/auth/me'),
  workspace: () => request<WorkspaceSummary>('/workspace/summary'),
  customers: (params: { q?: string; statusCode?: string; sort?: string; direction?: string; page?: number; pageSize?: number }) =>
    request<CustomerList>(`/customers?${search(params)}`),
  /** Satış müşteri seçici (09.10.2026): bütün portföyde arar, en az 2 karakter, en çok 20 satır. */
  searchCustomers: (q: string) =>
    request<{ items: CustomerListItem[]; limit: number }>(`/customers/search?${search({ q })}`),
  customer: (id: string) => request<CustomerDetail>(`/customers/${id}`),
  customerSalesSummary: (id: string) => request<CustomerSalesSummary>(`/customers/${id}/sales-summary`),
  // Fatura bilgisi (04.10.2026): kesilecek fatura kuyruğunun "adres, ülke, kimlik" eksiklerini kapatır.
  updateCustomerBilling: (id: string, body: { expectedVersion: number; billingAddress?: string; countryCode?: string; nationalId?: string; passportNumber?: string; taxNumber?: string }) =>
    request<CustomerDetail>(`/customers/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  addCustomerNote: (id: string, body: string) =>
    request<{ id: string; body: string; createdAt: string; authorId: string }>(`/customers/${id}/notes`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),
  // CRM-B4: GeziPuan defteri. `finans/gezipuan/:customerId` sadece kural motoru için (makine
  // kapsamı, `RequireMachineScopes`); operatör ekranı `finance/gezipuan/:customerId`i çağırır.
  gezipuanLedger: (customerId: string, movements = 50) =>
    request<GezipuanLedger>(`/finance/gezipuan/${customerId}?movements=${movements}`),
  // CRM-B9: kanal kimliği. `channel_identity:read`/`channel_identity:write` yetkisi ister
  // (`api/src/common/rbac/permissions.ts`).
  customerChannels: (customerId: string) => request<CustomerChannels>(`/customers/${customerId}/channels`),
  linkCustomerChannel: (customerId: string, body: { kind: string; handle: string; note?: string }) =>
    request<CustomerChannelIdentity & { created: boolean }>(`/customers/${customerId}/channels`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  lookupChannel: (kind: string, handle: string) =>
    request<ChannelLookup>(`/customers/channels/lookup?${search({ kind, handle })}`),
  // --- dashboard (CRM-A14) -----------------------------------------------
  // Gezinme, tek tek izin adlarına değil rolün **gördüğü bölümlere** bağlı (tasarım §4.1).
  dashboardWebOrders: () => request<SiteSiparisOzetiVerisi>('/dashboard/web-orders'),
  dashboardSections: () => request<DashboardSectionIndex>('/dashboard/sections'),
  dashboardSection: (key: string) => request<DashboardSection>(`/dashboard/${key.toLowerCase()}`),

  boundaries: () => request<{ reservedDomainModules: { name: string; state: string }[]; integrations: { key: string; state: string }[] }>('/platform/boundaries'),
  integrations: () => request<IntegrationReadiness>('/integrations'),

  /**
   * CRM'in ilk adımı: satışçının kaydetmeden önce çarpması gereken mükerrer kontrolü.
   * `GET /customers/duplicates`in aynısı (`customers.service.ts`, `findDuplicates`) — telefon ve
   * e-postayı normalize edip eşleşen kayıtları döndürür. İkisi de boşsa uç 400 döner, o yüzden
   * çağıran taraf (`App.tsx`teki hızlı ekleme formu) en az biri doluyken çağırır.
   */
  findDuplicateCustomers: (params: { email?: string; phone?: string }) =>
    request<CustomerDuplicateProbe>(`/customers/duplicates?${search(params)}`),

  /**
   * `type` INDIVIDUAL ise `firstName`/`lastName`/`nationalId` (TC Kimlik No), COMPANY ise
   * `companyName`/`taxNumber` (vergi/ticaret sicil no) zorunlu — uç bunu `assertIdentity` ile
   * uyguluyor (`api/src/modules/customers/customers.service.ts`). İkisi de burada opsiyonel:
   * hangisi zorunlu uçta türe göre belli oluyor, burada tekrar edilmiyor.
   */
  createCustomer: (body: {
    type: 'INDIVIDUAL' | 'COMPANY';
    firstName?: string;
    lastName?: string;
    companyName?: string;
    taxNumber?: string;
    nationalId?: string;
    /** DTO'da opsiyonel (`api/src/modules/customers/customers.dto.ts`); telefon tek başına yeterli olabilir. */
    email?: string;
    phone?: string;
    /** Fiziksel/fatura adresi; tek satır, en fazla 140 karakter, isteğe bağlı (17.10.2026). */
    billingAddress?: string;
    /** Verilmezse sunucu varsayılan durumu kullanır; formlar göndermiyor (04.10.2026). */
    statusCode?: string;
  }) => request<CustomerListItem>('/customers', { method: 'POST', body: JSON.stringify(body) }),

  // --- sales -------------------------------------------------------------
  leads: (params: { status?: string } = {}) => request<Paged<Lead>>(`/sales/leads?pageSize=50&${new URLSearchParams(params).toString()}`),
  createLead: (body: { customerId: string; title: string; currencyCode: string; expectedValueMinor: number; ownerId?: string; source?: string }) =>
    request<Lead>('/sales/leads', { method: 'POST', body: JSON.stringify(body) }),
  convertLead: (id: string, body: { expectedVersion: number }) => request<Opportunity>(`/sales/leads/${id}/convert`, { method: 'POST', body: JSON.stringify(body) }),
  salesProducts: (params: { q?: string; currencyCode?: string; offset?: number; limit?: number } = {}) =>
    request<{ items: SalesProduct[]; total: number; offset: number; limit: number }>(`/sales/products?${search(params)}`),
  updateSalesProduct: (id: string, body: { name: string; itemType: string; description?: string; supplierName?: string; currencyCode: string; unitPriceMinor: number; taxRateBps: number }) =>
    request<SalesProduct>(`/sales/products/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  createSalesProduct: (body: { name: string; itemType: string; description?: string; supplierName?: string; currencyCode: string; unitPriceMinor: number; taxRateBps?: number }) =>
    request<SalesProduct>('/sales/products', { method: 'POST', body: JSON.stringify(body) }),
  opportunities: (params: { q?: string; stage?: string } = {}) => {
    const search = new URLSearchParams({ pageSize: '50' });
    if (params.q) search.set('q', params.q);
    if (params.stage) search.set('status', params.stage);
    return request<Paged<Opportunity>>(`/sales/opportunities?${search.toString()}`);
  },
  createOpportunity: (body: { customerId: string; leadId?: string; title: string; currencyCode: string; expectedValueMinor: number; ownerId?: string; probability?: number; nextActionAt?: string; notes?: string }) =>
    request<Opportunity>('/sales/opportunities', { method: 'POST', body: JSON.stringify(body) }),
  /** `nextActionAt: null` (12.09.2026, BE06) takip tarihini **temizler**; alanı hiç göndermemek "dokunma". */
  updateOpportunity: (id: string, body: { expectedVersion: number; ownerId?: string; probability?: number; nextActionAt?: string | null; notes?: string; activityNote?: string }) =>
    request<Opportunity>(`/sales/opportunities/${id}/details`, { method: 'PATCH', body: JSON.stringify(body) }),
  changeStage: (id: string, body: { stage: string; expectedVersion: number; reason?: string }) =>
    request<Opportunity>(`/sales/opportunities/${id}/stage`, { method: 'PATCH', body: JSON.stringify(body) }),
  /**
   * 12.09.2026, BE01. Kayıtlı işe dönüşün okuma ucu: kaynak talep özeti ve bağlı tekliflerin
   * sürüm durumu. Talepler satırından aynı fırsat kimliğiyle açılır; kaynak mesaj tekrar POST
   * edilerek yeni talep oluşturulmaz.
   */
  opportunity: (id: string) => request<OpportunityDetail>(`/sales/opportunities/${id}`),
  quotes: () => request<Paged<Quote>>('/sales/quotes?pageSize=20'),
  quotePaymentStatus: (id: string, versionNo: number) => request<{ provider: string | null; paymentStatus: string; paidMinor?: number; currencyCode?: string; verifiedAt?: string | null; paymentUrl?: string; providerDurumu?: string | null; uyari?: 'LINK_CANCELLED' | 'LINK_EXPIRED' | 'QUOTE_EXPIRED' | null }>(`/sales/quotes/${id}/versions/${versionNo}/payment-status`),
  quote: (id: string) => request<Quote>(`/sales/quotes/${id}`),
  createQuote: (body: { opportunityId: string; items: QuoteItemInput[]; discountMinor?: number; taxMode?: 'NONE' | 'EXCLUSIVE' | 'INCLUSIVE'; taxRateBps?: number; notes?: string; validUntil?: string }) =>
    request<Quote>('/sales/quotes', { method: 'POST', body: JSON.stringify({ pricingMode: 'MANUAL_LOCAL', ...body }) }),
  /**
   * `sourceQuoteVersionId` (12.09.2026, BE04): revizyonun başladığı sürümün **kimliği**, ticari
   * sürüm numarası değil. "1. sürüm" iki ayrı teklifte de var; kaynak kimliği olamaz. Sunucu
   * kimliğin bu teklife ait olduğunu doğruluyor, başka teklifinki 400 alıyor.
   */
  addQuoteVersion: (id: string, body: { expectedQuoteVersion: number; sourceQuoteVersionId?: string; items: QuoteItemInput[]; discountMinor?: number; taxMode?: 'NONE' | 'EXCLUSIVE' | 'INCLUSIVE'; taxRateBps?: number; notes?: string; validUntil?: string }) =>
    request<Quote>(`/sales/quotes/${id}/versions`, { method: 'POST', body: JSON.stringify(body) }),
  publishQuote: (id: string, body: { versionNo: number; expectedQuoteVersion: number }) =>
    request<Quote>(`/sales/quotes/${id}/publish`, { method: 'POST', body: JSON.stringify(body) }),
  acceptQuote: (id: string, body: { versionNo: number; expectedQuoteVersion: number; idempotencyKey: string }) =>
    request<Quote & { idempotentReplay: boolean }>(`/sales/quotes/${id}/accept`, { method: 'POST', body: JSON.stringify(body) }),
  /**
   * Satış akışı vakası madde 13: "teklifi yenile." Kalem listesi gönderilmiyor — uç her kalemin
   * fiyatını kendi yeniden sorar, eski fiyat ne gidiyor ne dönüyor. `renewalWarnings`: katalog
   * bağı olmayan (elle yazılmış) ya da fiyatı yenilenemeyen kalemler için okunur cümle.
   */
  renewQuote: (id: string, body: { expectedQuoteVersion: number }) =>
    request<Quote & { renewalWarnings: string[] }>(`/sales/quotes/${id}/renew`, { method: 'POST', body: JSON.stringify(body) }),
  /**
   * Müşterinin "hayır" demesinin kaydı. `quote:accept` ister — kabulü kim yazıyorsa reddi de o
   * yazıyor, çünkü ikisi de aynı konuşmanın sonucu.
   *
   * Geri alınamaz: teklif ve sürüm `REJECTED`e geçiyor, ve durum makinesi oradan geri bir yol
   * tanımıyor. Ekran bu yüzden gerekçeyi ve açık bir onayı istemeden çağırmıyor.
   */
  rejectQuote: (id: string, body: { versionNo: number; expectedQuoteVersion: number; reason: string }) =>
    request<Quote>(`/sales/quotes/${id}/reject`, { method: 'POST', body: JSON.stringify(body) }),
  /**
   * FR-24: alt marj eşiğinin altında kalıp taslakta bekleyen bir sürümü yayımlanabilir hâle
   * getirir. `quote:margin_approve` yalnız MANAGER'da — şartnamedeki "yönetici onayı" kelimesi
   * kelimesine bu rolü işaret ediyor; Finans bu izni tutmuyor çünkü onay bir satış kararı.
   *
   * Uç eşik altında **olmayan** bir sürümde 400 dönüyor ("onay gerekmiyor") ve ekran o cümleyi
   * olduğu gibi yazıyor: onay gerektirmeyen bir sürümü onaylamış gibi göstermek, onayın kendisini
   * anlamsızlaştırırdı.
   */
  approveQuoteMargin: (id: string, body: { versionNo: number; expectedQuoteVersion: number; note?: string }) =>
    request<Quote>(`/sales/quotes/${id}/margin-approval`, { method: 'POST', body: JSON.stringify(body) }),

  // --- teklif asistanı: talep alma (satis-akisi-vaka.md adım 2-3) --------
  /**
   * Serbest metin girer, yapılandırılmış alanlar çıkar (FR-01, FR-02). Ayrıştırma başarısız
   * olsa bile uç **201** dönüyor ve talebi yazıyor: operasyon durmuyor, alanlar elle
   * doldurulabilir kalıyor. Ne olduğu `parsingStatus`/`parsingReasonText`te.
   *
   * `?beklemesiz=1`: uç talebi hemen yazıp döner (`parsedAt: null`), ayrıştırma arka planda sürer;
   * ekran `salesRequest` ile yoklar.
   */
  createSalesRequest: (body: { rawText: string }) =>
    request<QuoteRequest>('/sales/requests?beklemesiz=1', { method: 'POST', body: JSON.stringify(body) }),
  /** Tek vize talebi: model çağrılmaz, alanlar kuraldan gelir (`fieldMeta` kaynağı `RULE`). */
  createVisaSalesRequest: (body: { rawText: string; visaDurationDays: number; entries: 'SINGLE' | 'MULTIPLE'; adultCount: number; childCount?: number; childAges?: number[] }) =>
    request<QuoteRequest>('/sales/requests/visa', { method: 'POST', body: JSON.stringify(body) }),
  /**
   * Gelen talepler, en yenisi başta (`listRequests`, `orderBy: createdAt desc`).
   *
   * Ölçülen kusur: uç 27.08'den beri açıktı ve hiçbir ekran çağırmıyordu — bir talep yalnız onu
   * oluşturan oturum boyunca bulunabiliyordu. Sayfa yenilenince kayıt "yok" oluyordu; oysa
   * veritabanında duruyordu.
   */
  salesRequests: (params: { status?: string; page?: number; pageSize?: number } = {}) =>
    request<Paged<QuoteRequest>>(`/sales/requests?${search(params)}`),
  salesRequest: (id: string) => request<QuoteRequest>(`/sales/requests/${id}`),
  /** FR-01: düzeltilen alan `MANUAL`/tam güvene geçer, düşük güven işareti kalkar. */
  updateSalesRequestFields: (id: string, body: QuoteRequestFieldPatch) =>
    request<QuoteRequest>(`/sales/requests/${id}/fields`, { method: 'PATCH', body: JSON.stringify(body) }),
  /** FR-03: onay olmadan teklif üretimine geçilmez; onay bir müşteri kaydı zorunlu kılıyor. */
  confirmSalesRequest: (id: string, body: { expectedVersion: number; customerId?: string }) =>
    request<QuoteRequest>(`/sales/requests/${id}/confirm`, { method: 'POST', body: JSON.stringify(body) }),
  createOpportunityFromSalesRequest: (id: string, body: { expectedVersion: number; ownerId?: string }) =>
    request<Opportunity>(`/sales/requests/${id}/create-opportunity`, { method: 'POST', body: JSON.stringify(body) }),
  /** Vaka adım 4'ün fiyat kutusu. Tedarikçiye gitmez, yazılmış satırı okur — anında döner. */
  salesRequestPriceLookup: (id: string) => request<PriceLookupView>(`/sales/requests/${id}/price-lookup`),
  itemSuggestions: (params: { q: string; language: string; currency: string; kind?: string; limit?: number }) =>
    request<ItemSuggestion[]>(`/sales/item-suggestions?${search(params)}`),

  // --- teklif asistanı: fiyat önerisi (satis-akisi-vaka.md adım 4-5) -----
  /** Var olan bir tedarikçi sorgusunu okur, tedarikçiye gitmez. */
  priceRecommendation: (params: { slug: string; destination?: string; serviceDate?: string; customerId?: string }) =>
    request<PriceRecommendationView>(`/sales/price-recommendation?${search(params)}`),
  /**
   * Gerçek bir Paximum sorgusu açar, ama yalnız `product.refreshable` true iken (`kind ===
   * 'HOTEL' && supplierCode === 'paximum'`); başka her üründe `priceRecommendation`in
   * vereceğiyle birebir aynı cevabı verir. Beş dakikalık soğutma sunucu tarafında.
   */
  refreshPriceRecommendation: (body: {
    slug: string;
    destination?: string;
    serviceDate?: string;
    customerId?: string;
    checkIn: string;
    checkOut: string;
    rooms: PriceRecommendationRoomInput[];
    nationality: string;
  }) => request<PriceRecommendationView>('/sales/price-recommendation/refresh', { method: 'POST', body: JSON.stringify(body) }),

  // --- teklif asistanı: ödeme bağlantısı (satis-akisi-vaka.md adım 9) ----
  createPaymentLink: (quoteId: string, versionNo: number) =>
    request<PaymentLinkResult>(`/sales/quotes/${quoteId}/versions/${versionNo}/payment-link`, { method: 'POST', body: '{}' }),

  // --- teklif çıktıları: proforma PDF'i, müşteri bağlantısı, WhatsApp (vaka adım 6-9) ----
  /**
   * FR-40 proformanın kendisi. `download()` kullanılıyor, `request()` değil: uç
   * `application/pdf` dönüyor ve `request()` her cevabı JSON olarak ayrıştırmaya çalışır.
   *
   * Uç yalnız `PUBLISHED`/`ACCEPTED` bir sürümü çiziyor (`loadCustomerSafeVersion`); taslakta
   * 404 döner. Ekran bu yüzden taslak sürümde düğmeyi edilgen çiziyor ve gerekçesini yazıyor,
   * ama kapı orada değil burada, sunucuda.
   */
  hotelRoomGallery: (slug: string, names: readonly string[], signal?: AbortSignal) => request<Pick<HotelRoomPhotos, 'rooms'>>(`/catalogue/hotels/${slug}/room-gallery?${new URLSearchParams(names.map(name => ['names', name])).toString()}`, { signal }),
  hotelRoomPhotos: (slug: string, signal?: AbortSignal) => request<HotelRoomPhotos>(`/catalogue/hotels/${slug}/photos`, { signal }),
  hotelAvailability: (slug: string, input: HotelStayInput) => request<HotelAvailabilityResult>(`/catalogue/hotels/${slug}/availability`, { method: 'POST', body: JSON.stringify(input) }),
  downloadQuoteDocument: (quoteId: string) => download(`/sales/quotes/${quoteId}/document`, 'belge.pdf', true),
  downloadQuotePdf: (quoteId: string, versionNo: number, quoteCode: string) =>
    download(`/sales/quotes/${quoteId}/versions/${versionNo}/pdf`, `proforma-${quoteCode}-s${String(versionNo)}.pdf`),
  /**
   * Vaka madde 9'un tuşu: proforma **ve** MamoPay bağlantısı müşteriye gider.
   *
   * `createPaymentLink`in yerine geçmiyor, onu **içeriyor**: uç aynı idempotent
   * `createOrGet`i çağırıyor, yani bu düğmeye basmak ikinci bir MamoPay bağlantısı açmaz.
   */
  sendQuotePaymentLinkOnWhatsApp: (quoteId: string, versionNo: number) =>
    request<QuoteWhatsAppResult>(`/sales/quotes/${quoteId}/versions/${versionNo}/whatsapp/payment-link`, { method: 'POST', body: '{}' }),
  /**
   * Genel gönderim katmanı. Rıza, sağlayıcı, 24 saatlik pencere/şablon ve medya sınırı
   * kapılarının hepsi uçta; burada tekrarlanan hiçbir kontrol yok ve bir "zorla gönder"
   * bayrağı yok. Reddedilen bir gönderim de 201 döner, `ok: false` ile.
   */
  sendWhatsAppMessage: (body: { to: string; purpose: WhatsAppAmaci; text?: string; language?: string }) =>
    request<WhatsAppSendResult>('/whatsapp/send', { method: 'POST', body: JSON.stringify(body) }),

  // --- orders ------------------------------------------------------------
  orders: (params: { customerId?: string; status?: string; q?: string; page?: number; pageSize?: number } = {}) =>
    request<Paged<OrderListItem>>(`/orders?${search({ pageSize: 20, ...params })}`),
  order: (id: string) => request<Order>(`/orders/${id}`),
  createOrder: (body: { quoteId: string; idempotencyKey: string }) =>
    request<{ order: Order; idempotentReplay: boolean }>('/orders', { method: 'POST', body: JSON.stringify(body) }),
  transitionOrder: (id: string, body: { status: string; expectedVersion: number; reason?: string }) =>
    request<Order>(`/orders/${id}/status`, { method: 'PATCH', body: JSON.stringify(body) }),

  // --- fulfilment --------------------------------------------------------
  tasks: (orderId: string) => request<{ items: FulfilmentTask[]; total: number }>(`/fulfilment/tasks?orderId=${orderId}`),
  updateTaskDetails: (
    id: string,
    body: { expectedVersion: number; travellerName?: string; operationalNote?: string; checklist?: ChecklistEntry[] },
  ) => request<FulfilmentTask>(`/fulfilment/tasks/${id}/details`, { method: 'PATCH', body: JSON.stringify(body) }),
  updateTaskOperations: (id: string, body: { expectedVersion: number; priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT'; dueAt?: string }) =>
    request<FulfilmentTask>(`/fulfilment/tasks/${id}/operations`, { method: 'PATCH', body: JSON.stringify(body) }),
  addTaskNote: (id: string, body: string) => request(`/fulfilment/tasks/${id}/notes`, { method: 'POST', body: JSON.stringify({ body }) }),
  uploadTaskDocument: (body: { orderId: string; fulfilmentTaskId: string; kind: string; fileName: string; contentType: string; contentBase64: string }) =>
    request<TaskDocument>('/documents', { method: 'POST', body: JSON.stringify(body) }),
  downloadTaskDocument: (id: string, fileName: string) => download(`/documents/${id}/content`, fileName),
  /**
   * Vize destek belgesi üretir ve göreve bağlı belge olarak saklar (izin `document:write`).
   *
   * Dönen PDF ekrana inmiyor: uç onu `DocumentAsset` deposuna yazıyor ve indirme yine
   * `downloadTaskDocument` üzerinden, bütünlük kontrolü ve denetim kaydıyla birlikte yürüyor.
   */
  uretVizeBelgesi: (taskId: string, body: VizeBelgeIstegi) =>
    request<VizeBelgeUretimSonucu>(`/operations/tasks/${taskId}/vize-belgesi`, { method: 'POST', body: JSON.stringify(body) }),
  /**
   * Siparişten ve görevden bağımsız belge üretimi (izin `visa-document:generate`).
   *
   * Ürün Sahibi, 10.09.2026: sipariş WhatsApp'tan geliyor ve belge CRM kaydı beklemeden
   * basılabilmeli. `yolcular` burada zorunlu: düşecek bir görev kaydı yok.
   */
  uretSerbestVizeBelgesi: (body: VizeBelgeIstegi & { customerId?: string }) =>
    request<VizeBelgeUretimSonucu>('/operations/vize-belgesi', { method: 'POST', body: JSON.stringify(body) }),
  /** MBD otel kataloğunda arama (yayındaki oteller, izin `document:read`). */
  vizeOtelAra: (q: string, signal?: AbortSignal) =>
    request<{ oteller: VizeOtelOzeti[]; katalogHatasi?: boolean }>(`/operations/vize-belge/oteller?${search({ q })}`, { signal }),
  /** Seçilen katalog otelinin belge kaydı; eksik alanlar `eksik` listesinde. */
  vizeOtelDetay: (slug: string, signal?: AbortSignal) =>
    request<VizeOtelKaydi>(`/operations/vize-belge/oteller/${encodeURIComponent(slug)}`, { signal }),
  /** Son üretilen siparişsiz vize belgeleri, en yenisi üstte (izin `document:read`). */
  /** Sayfalı (11.09.2026): `page` 1'den başlar, `pageSize` varsayılan 20, tavan 100. */
  serbestVizeBelgeleri: (params: { page?: number; pageSize?: number } = {}) =>
    request<Paged<SerbestBelge>>(`/operations/vize-belgeleri?${search(params)}`),
  /** Belge arşivi: üretilen ve yüklenen bütün belgeler, sayfalı (izin `document:read`). */
  belgeArsivi: (params: BelgeArsivSorgu = {}) => request<Paged<BelgeArsivSatiri>>(`/document-archive?${search(params)}`),
  /** Siparişsiz belgeyi CRM siparişine ya da web siparişine bağlar (izin `document:write`). */
  belgeSipariseBagla: (id: string, body: { orderCode: string } | { webOrderReference: string }) =>
    request<unknown>(`/document-archive/${encodeURIComponent(id)}/order`, { method: 'PUT', body: JSON.stringify(body) }),
  downloadOperationsCsv: () => download('/exports/orders.csv', 'mbd-operations-orders.csv'),
  downloadFinanceCsv: () => download('/exports/orders.csv', 'mbd-finance-orders.csv'),
  assignTask: (id: string, body: { assignedToId: string; expectedVersion: number; dueAt?: string }) =>
    request<FulfilmentTask>(`/fulfilment/tasks/${id}/assignee`, { method: 'PATCH', body: JSON.stringify(body) }),
  transitionTask: (id: string, body: { status: string; expectedVersion: number; reason?: string }) =>
    request<FulfilmentTask>(`/fulfilment/tasks/${id}/status`, { method: 'PATCH', body: JSON.stringify(body) }),
  createBooking: (id: string, body: { supplierName: string; expectedTaskVersion: number; notes?: string }) =>
    request<SupplierBooking>(`/fulfilment/tasks/${id}/bookings`, { method: 'POST', body: JSON.stringify(body) }),
  transitionBooking: (id: string, body: { status: string; expectedVersion: number; supplierReference?: string; reason?: string }) =>
    request<SupplierBooking>(`/fulfilment/bookings/${id}/status`, { method: 'PATCH', body: JSON.stringify(body) }),
  issueVoucher: (id: string, body: { expectedTaskVersion: number; travellerName: string; instructions?: string }) =>
    request<Voucher>(`/fulfilment/tasks/${id}/voucher`, { method: 'POST', body: JSON.stringify(body) }),

  // --- Satış akışı vakası madde 16-18: operasyon takvimi, tedarikçi bildirimi, tedarikçi maliyeti
  operationsCalendar: (params: { from?: string; to?: string } = {}) =>
    request<OperationsCalendar>(`/operations/calendar?${search(params)}`),
  supplierNotifications: (params: { orderId?: string; state?: string } = {}) =>
    request<{ items: SupplierNotificationRow[]; total: number }>(`/operations/supplier-notifications?${search(params)}`),
  markSupplierNotified: (itemId: string, body: { expectedVersion?: number; note?: string } = {}) =>
    request<SupplierNotificationRow>(`/operations/supplier-notifications/${itemId}/sent`, { method: 'POST', body: JSON.stringify(body) }),
  /** `finance:write`teki `recordCost` ile aynı tabloya yazar, ama tutar geri dönmez (bkz. tip). */
  supplierCostStatus: (itemId: string) => request<SupplierCostStatus>(`/operations/supplier-costs/${itemId}`),
  recordSupplierCost: (itemId: string, body: { amountMinor: number; currencyCode: string; note?: string }) =>
    request<{ id: string; orderItemId: string; recordedAt: string }>(`/operations/supplier-costs/${itemId}`, { method: 'POST', body: JSON.stringify(body) }),

  // --- finance -----------------------------------------------------------
  financeSummary: (orderId: string) => request<FinanceSummary>(`/finance/orders/${orderId}/summary`),
  recordPayment: (
    orderId: string,
    body: { kind: string; amountMinor: number; currencyCode: string; method: string; reference: string; idempotencyKey: string },
  ) => request<{ payment: PaymentEvidence; idempotentReplay: boolean }>(`/finance/orders/${orderId}/payments`, { method: 'POST', body: JSON.stringify(body) }),
  verifyPayment: (id: string, body: { expectedVersion: number; confirmedAmountMinor: number }) =>
    request<{ payment: PaymentEvidence; idempotentReplay: boolean; summary: FinanceSummary }>(`/finance/payments/${id}/verify`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /**
   * Doğrulamanın karşı yarısı: kanıt gerçek değil, tutar tutmuyor, dekont başka bir siparişe ait.
   * `payment:verify` ister — kanıtı para sayan da saymayan da aynı yetki.
   *
   * Geri alınamaz: durum makinesi `REJECTED`ten geri bir yol tanımıyor, yani yanlış reddedilen
   * bir kanıtın karşılığı ikinci bir kayıttır. Ekran bu yüzden gerekçe ve açık onay istiyor.
   */
  rejectPayment: (id: string, body: { expectedVersion: number; reason: string }) =>
    request<PaymentEvidence>(`/finance/payments/${id}/reject`, { method: 'POST', body: JSON.stringify(body) }),
  /**
   * Faturanın kesileceği kuru dondurur. **Faturadan önce gelir**: sipariş fatura para biriminden
   * başka bir para birimindeyse kur dondurulmadan kesme çağrısı 409 `INVOICE_NOT_READY` döner.
   *
   * `rateMicros` tam sayı: 1 birim sipariş parası kaç **mikro** fatura parası. Ondalıklı bir kur
   * hiçbir aşamada taşınmıyor; ekran kullanıcının yazdığını tam sayı aritmetiğiyle çeviriyor.
   * `rateDate` **paranın geldiği gün**, faturanın kesildiği gün değil.
   */
  freezeInvoiceFx: (
    orderId: string,
    body: { rateMicros: number; source: string; rateDate: string; expectedVersion: number },
  ) => request<InvoiceFxResult>(`/finance/orders/${orderId}/invoice-fx`, { method: 'POST', body: JSON.stringify(body) }),
  recordCost: (
    orderId: string,
    body: { category: string; supplierName: string; description: string; amountMinor: number; currencyCode: string; orderItemId?: string },
  ) => request<CostEntry>(`/finance/orders/${orderId}/costs`, { method: 'POST', body: JSON.stringify(body) }),
  requestRefund: (orderId: string, body: { amountMinor: number; currencyCode: string; reason: string }) =>
    request<RefundRecord>(`/finance/orders/${orderId}/refunds`, { method: 'POST', body: JSON.stringify(body) }),
  decideRefund: (id: string, body: { decision: 'APPROVED' | 'REJECTED'; expectedVersion: number; note: string }) =>
    request<RefundRecord>(`/finance/refunds/${id}/decision`, { method: 'POST', body: JSON.stringify(body) }),
  reconcile: (orderId: string, body: { status: string; expectedVersion: number; reason?: string }) =>
    request<Reconciliation>(`/finance/orders/${orderId}/reconciliation`, { method: 'PATCH', body: JSON.stringify(body) }),

  // --- Kampanya akışı vakası madde 17-26: influencer hakedişleri ----------
  // Finans'ın kendi ekranı, `finance:read`/`finance:write`. Mutabakat gönderimi, itiraz ve onay
  // burada elle çalıştırılıyor: aylık takvim ve hatırlatma otomasyonu henüz kod taşımıyor
  // (`influencer-earning.service.ts` üstündeki not).
  influencerEarnings: (params: { status?: string; externalInfluencerId?: string; externalCampaignId?: string; page?: number; pageSize?: number } = {}) =>
    request<InfluencerEarningListPage>(`/finance/influencer-earnings?${search(params)}`),
  influencerEarning: (id: string) => request<InfluencerEarningDetail>(`/finance/influencer-earnings/${id}`),
  sendInfluencerReconciliation: (id: string) =>
    request<InfluencerEarning>(`/finance/influencer-earnings/${id}/send-reconciliation`, { method: 'POST', body: '{}' }),
  disputeInfluencerEarning: (id: string, body: { reason: string }) =>
    request<InfluencerEarning>(`/finance/influencer-earnings/${id}/dispute`, { method: 'POST', body: JSON.stringify(body) }),
  approveInfluencerEarning: (id: string) =>
    request<InfluencerEarning>(`/finance/influencer-earnings/${id}/approve`, { method: 'POST', body: '{}' }),
  resolveInfluencerDispute: (id: string, body: { outcome: 'NO_CHANGE' | 'CORRECTED'; correctedAmountMinor?: number; note?: string }) =>
    request<InfluencerEarning>(`/finance/influencer-earnings/${id}/resolve-dispute`, { method: 'POST', body: JSON.stringify(body) }),
  uploadInfluencerInvoice: (id: string, body: { fileName: string; contentType: string; contentBase64: string; note?: string }) =>
    request<InfluencerEarningInvoice>(`/finance/influencer-earnings/${id}/invoice`, { method: 'POST', body: JSON.stringify(body) }),
  downloadInfluencerInvoice: (invoiceId: string, fileName: string) => download(`/finance/influencer-earnings/invoices/${invoiceId}/download`, fileName),
  recordInfluencerPaymentOrder: (id: string, body: { reference: string; note?: string }) =>
    request<InfluencerEarning>(`/finance/influencer-earnings/${id}/payment-order`, { method: 'POST', body: JSON.stringify(body) }),
  markInfluencerPaid: (id: string, body: { note?: string } = {}) =>
    request<InfluencerEarning>(`/finance/influencer-earnings/${id}/paid`, { method: 'POST', body: JSON.stringify(body) }),

  // --- Satış akışı vakası madde 19-20: kesilen fatura ve tedarikçi ödemesi -
  // Kesilecek fatura kuyruğu (04.10.2026): ödenmiş web siparişi, alış faturası, satış faturası.
  invoiceQueue: (params: { includeIssued?: boolean; page?: number; pageSize?: number } = {}) =>
    request<InvoiceQueuePage>(`/finance/invoice-queue?${search(params)}`),
  invoiceQueueDetail: (reference: string) => request<InvoiceQueueDetail>(`/finance/invoice-queue/${encodeURIComponent(reference)}`),
  freezeWebOrderFx: (reference: string, body: { rateMicros: number; source: string; rateDate: string }) =>
    request<{ idempotentReplay: boolean; rateMicros: number }>(`/finance/invoice-queue/${encodeURIComponent(reference)}/fx`, { method: 'POST', body: JSON.stringify(body) }),
  recordPurchaseInvoice: (reference: string, body: PurchaseInvoiceInput) =>
    request<PurchaseInvoiceRow>(`/finance/invoice-queue/${encodeURIComponent(reference)}/purchase-invoices`, { method: 'POST', body: JSON.stringify(body) }),
  voidPurchaseInvoice: (reference: string, id: string, reason: string) =>
    request<PurchaseInvoiceRow>(`/finance/invoice-queue/${encodeURIComponent(reference)}/purchase-invoices/${encodeURIComponent(id)}/void`, { method: 'POST', body: JSON.stringify({ reason }) }),
  // Fatura kaydı (08.10.2026): satış faturası PayPartner'da kesilir, CRM yalnız kaydını tutar.
  updatePurchaseInvoice: (reference: string, id: string, body: Partial<PurchaseInvoiceInput>) =>
    request<PurchaseInvoiceRow>(`/finance/invoice-queue/${encodeURIComponent(reference)}/purchase-invoices/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  recordSalesInvoice: (reference: string, body: SalesInvoiceRecordInput) =>
    request<{ invoice: WebSalesInvoice; warnings: string[] }>(`/finance/invoice-queue/${encodeURIComponent(reference)}/sales-record`, { method: 'POST', body: JSON.stringify(body) }),
  updateSalesInvoiceRecord: (reference: string, body: Partial<SalesInvoiceRecordInput>) =>
    request<{ invoice: WebSalesInvoice }>(`/finance/invoice-queue/${encodeURIComponent(reference)}/sales-record`, { method: 'PATCH', body: JSON.stringify(body) }),
  attachInvoiceDocument: (target: 'purchase' | 'sales', id: string, body: { fileName: string; contentType: string; contentBase64: string }) =>
    request<{ documentId: string; fileName: string }>(`/finance/invoice-documents/${target}/${encodeURIComponent(id)}`, { method: 'POST', body: JSON.stringify(body) }),
  downloadInvoiceDocument: (documentId: string, fileName: string) => download(`/finance/invoice-documents/${encodeURIComponent(documentId)}/download`, fileName),
  purchaseInvoices: (params: { q?: string; includeVoided?: boolean; page?: number; pageSize?: number } = {}) =>
    request<PurchaseInvoiceListPage>(`/finance/invoice-records/purchases?${search(params)}`),
  invoiceMargin: (params: { page?: number; pageSize?: number } = {}) => request<InvoiceMarginPage>(`/finance/invoice-records/margin?${search(params)}`),
  orderInvoices: (params: { state?: string; page?: number; pageSize?: number } = {}) =>
    request<OrderInvoiceListPage>(`/finance/order-invoices?${search(params)}`),
  orderInvoiceByOrder: (orderId: string) => request<OrderInvoiceRow>(`/finance/order-invoices/${orderId}`),
  payableSupplierCosts: (params: { paymentStatus?: string; orderId?: string; page?: number; pageSize?: number } = {}) =>
    request<SupplierPayableListPage>(`/finance/supplier-payments?${search(params)}`),
  supplierPaymentDetail: (costEntryId: string) => request<SupplierPaymentDetail>(`/finance/supplier-payments/${costEntryId}`),
  recordSupplierPaymentOrder: (costEntryId: string, body: { reference: string; note?: string }) =>
    request<SupplierPayment>(`/finance/supplier-payments/${costEntryId}/payment-order`, { method: 'POST', body: JSON.stringify(body) }),
  markSupplierPaymentPaid: (costEntryId: string, body: { note?: string } = {}) =>
    request<SupplierPayment>(`/finance/supplier-payments/${costEntryId}/paid`, { method: 'POST', body: JSON.stringify(body) }),
  uploadSupplierExpenseInvoice: (costEntryId: string, body: { fileName: string; contentType: string; contentBase64: string; note?: string }) =>
    request<SupplierExpenseInvoice>(`/finance/supplier-payments/${costEntryId}/invoice`, { method: 'POST', body: JSON.stringify(body) }),
  downloadSupplierExpenseInvoice: (invoiceId: string, fileName: string) => download(`/finance/supplier-payments/invoices/${invoiceId}/download`, fileName),

  // --- Kupon defteri (kupon akışı vakası §5) -------------------------------
  // Türkçe yollar (`finans/kuponlar/...`) yalnız makine tarafında; operatör ekranı burada
  // İngilizce `finance/coupons` yolunu çağırıyor (`coupon.module.ts`teki gerekçenin aynısı).
  coupons: (params: { influencerId?: string; campaignId?: string; status?: string; page?: number; pageSize?: number } = {}) =>
    request<CouponListPage>(`/finance/coupons?${search(params)}`),
  coupon: (code: string) => request<CouponDetail>(`/finance/coupons/${encodeURIComponent(code)}`),
  couponNotifications: (limit = 100) => request<CouponNotificationPage>(`/finance/coupons/notifications?${search({ limit })}`),
  /**
   * Uc `DrainReport` donuyor (`coupon-notification.deliverer.ts`), `{delivered, remaining}` degil.
   *
   * Olculdu 28.08.2026: bu sarmalayici olmayan iki alan okuyordu ve ekran "— teslim edildi, —
   * bekliyor." yaziyordu. Tip yanlisti, cagri degil; derleyici de bunu yakalayamaz cunku `request`
   * cevabi gelen tipe **inaniyor**.
   */
  deliverCouponNotifications: () => request<CouponDrainReport>('/finance/coupons/notifications/deliver', { method: 'POST', body: '{}' }),
  /** Pes edilmis (FAILED) satirlari kuyruga geri koyar. `coupon:write` ister. */
  requeueCouponNotifications: () => request<{ requeued: number }>('/finance/coupons/notifications/requeue', { method: 'POST', body: '{}' }),
  publishCoupon: (code: string) => request<CouponDetail>(`/finance/coupons/${encodeURIComponent(code)}/publish`, { method: 'POST', body: '{}' }),

  // --- Pazarlama: MBD'nin kendi kampanyaları ve geri kazanım (07.10.2026) -----------------
  campaigns: (source: 'ALL' | 'INFLUENCER' | 'INTERNAL' = 'ALL') => request<{ items: CampaignRow[] }>(`/marketing/campaigns?${search({ source })}`),
  campaign: (id: string) => request<CampaignDetail>(`/marketing/campaigns/${encodeURIComponent(id)}`),
  createCampaign: (body: CreateCampaignInput) => request<{ campaign: CampaignRow; codes: CampaignCode[] }>('/marketing/campaigns', { method: 'POST', body: JSON.stringify(body) }),
  addCampaignCodes: (id: string, codes: CampaignCodesInput) =>
    request<{ codes: CampaignCode[] }>(`/marketing/campaigns/${encodeURIComponent(id)}/codes`, { method: 'POST', body: JSON.stringify({ codes }) }),
  setCampaignStatus: (id: string, status: 'ACTIVE' | 'SUSPENDED') =>
    request<{ id: string; status: string; updated: number; unpublished: number }>(`/marketing/campaigns/${encodeURIComponent(id)}/status`, { method: 'POST', body: JSON.stringify({ status }) }),
  recoveryAutomations: () => request<RecoveryOverview>('/marketing/recovery/automations'),
  updateRecoveryAutomation: (trigger: RecoveryTriggerKey, body: RecoveryAutomationInput) =>
    request<RecoveryAutomation>(`/marketing/recovery/automations/${trigger}`, { method: 'PUT', body: JSON.stringify(body) }),
  recoveryHistory: (trigger: RecoveryTriggerKey, params: { outcome?: string; page?: number } = {}) =>
    request<RecoveryHistoryPage>(`/marketing/recovery/automations/${trigger}/history?${search(params)}`),
  runRecovery: () => request<RecoveryRunSummary[]>('/marketing/recovery/run', { method: 'POST', body: '{}' }),

  // --- GeziPuan borcu (finans kaydı) ---------------------------------------
  // `gezipuanLedger` (yukarıda) bir üyenin kendi defteri; bu, şirketin toplam borcu.
  gezipuanLiability: () => request<GezipuanLiability>('/finance/gezipuan/liability'),

  // --- FR-20: marj kuralları ----------------------------------------------
  // `margin_rule:write` üç uçta da aynı: listelemek de bir marj yüzdesini okumak demek, o yüzden
  // okuma ayrı bir izne bağlanmadı (`margin-rule.module.ts`in kendi gerekçesi).
  //
  // `active` süzgeci **gönderilmiyor**: uçtaki alan `@IsBoolean()` ile tanımlı ve sorgu dizesinden
  // gelen `"true"` bir dize; ekranın işi de zaten pasif kuralları göstermek, çünkü bir kuralı geri
  // açmanın tek yolu onu görmek.
  marginRules: () => request<MarginRule[]>('/pricing/margin-rules'),
  createMarginRule: (body: {
    name: string;
    priority?: number;
    destination?: string;
    itemType?: string;
    seasonStartMonth?: number;
    seasonEndMonth?: number;
    customerSegmentTag?: string;
    minAmountMinor?: number;
    maxAmountMinor?: number;
    bandCurrencyCode?: string;
    marginBps?: number;
    marginFixedMinor?: number;
    decidedAt: string;
  }) => request<MarginRule>('/pricing/margin-rules', { method: 'POST', body: JSON.stringify(body) }),
  setMarginRuleActive: (id: string, body: { active: boolean; expectedVersion: number }) =>
    request<MarginRule>(`/pricing/margin-rules/${id}/active`, { method: 'PATCH', body: JSON.stringify(body) }),

  // --- NFR-04: istem şablonları -------------------------------------------
  // Okumak `config:read` (her rolde var), yazmak `config:write` (yalnız ADMIN).
  //
  // `GET active/:key` **çağrılmıyor**: liste zaten hangi sürümün aktif olduğunu taşıyor
  // (`active: true`), ve o uç aktif sürüm yokken `null` döndürüyor — gövdesiz bir cevabı
  // buradaki `request()` boş bir nesneye çeviriyor, yani "yok" ile "okunamadı" ayırt edilemezdi.
  promptTemplates: (params: { key?: string } = {}) =>
    request<PromptTemplate[]>(`/ai/prompt-templates?${search(params)}`),
  createPromptTemplate: (body: { key: IstemSablonuAnahtari; body: string; changeNote?: string; active?: boolean }) =>
    request<PromptTemplate>('/ai/prompt-templates', { method: 'POST', body: JSON.stringify(body) }),
  activatePromptTemplate: (id: string) =>
    request<PromptTemplate>(`/ai/prompt-templates/${id}/activate`, { method: 'POST', body: '{}' }),

  // --- oversight ---------------------------------------------------------
  oversight: (orderId: string) => request<Oversight>(`/oversight/orders/${orderId}`),
  managerExceptions: () => request<ManagerExceptions>('/oversight/exceptions'),

  // --- MBDTravel v3 (Commerce authority) ---------------------------------
  // Read-only. There is no write call in this section and there is no local copy of any of
  // it: every screen below asks v3 again rather than reading something the CRM cached.
  salesReport: (params: SalesReportParams) => request<SalesReport>(`/sales/report?${search(params)}`),
  v3Orders: (params: { status?: string; limit?: number; offset?: number } = {}) =>
    request<V3OrderList>(`/operations/orders?${search(params)}`),
  v3Order: (reference: string, language: string) =>
    request<V3OrderDetail>(`/operations/orders/${encodeURIComponent(reference)}?${search({ language })}`),
  releaseQueue: (params: { limit?: number } = {}) => request<ReleaseQueue>(`/operations/release-queue?${search(params)}`),
  receivables: (params: { invoiceStatus?: string; limit?: number; offset?: number } = {}) =>
    request<Receivables>(`/finance/receivables?${search(params)}`),
  visaVariants: (signal?: AbortSignal) => request<VisaVariants>('/catalogue/visa-variants', { signal }),
  supplierOptions: () => request<{ items: { id: string; code: string; name: string; kind: string }[] }>('/finance/suppliers/options'),
  catalogue: (params: CatalogueParams, signal?: AbortSignal) => request<ProductList>(`/catalogue/products?${search(params)}`, { signal }),
  /** Katalog seçicisi: ürün ağacının tamamı tek yanıtta, sayfalama sunucuda (`V3Service.catalogueSummary`). */
  catalogueSummary: (currency: string, signal?: AbortSignal) =>
    request<{ products: Product[]; total: number }>(`/catalogue/product-summary?${search({ currency })}`, { signal }),
  catalogueDepartures: (slug: string, params: { language: string; currency: string }) =>
    request<DepartureList>(`/catalogue/products/${encodeURIComponent(slug)}/departures?${search(params)}`),

  changeWebSupportCaseState: (reference: string, state: 'ANSWERED' | 'CLOSED') =>
    request<{ reference: string; state: string; changed: boolean }>(`/webshop/support-cases/${encodeURIComponent(reference)}/state`, { method: 'POST', body: JSON.stringify({ state }) }),
  markWebSupportSpam: (reference: string, spam: boolean) =>
    request<{ reference: string; spam: boolean; state: string; changed: boolean }>(`/webshop/support-cases/${encodeURIComponent(reference)}/spam`, { method: 'POST', body: JSON.stringify({ spam }) }),
  webPaymentAttempts: (params: WebPaymentsQuery) => request<WebPayments>(`/webshop/payments?${search(params)}`),
  webPanelOverview: () => request<WsOverview>('/webshop/panel/overview'),
  webPanelInbox: (params: WsSorgu) => request<WsTalepler>(`/webshop/panel/inbox?${search(params)}`),
  webPanelMembers: (params: WsSorgu) => request<WsUyeler>(`/webshop/panel/members?${search(params)}`),
  webPanelMember: (externalId: string) => request<WsKisi>(`/webshop/panel/members/${encodeURIComponent(externalId)}`),
  webPanelOrders: (params: WsSorgu) => request<WsSiparisler>(`/webshop/panel/orders?${search(params)}`),
  webPanelItem: (tur: WsTur, reference: string) => request<WsCekmece>(`/webshop/panel/item/${tur}/${encodeURIComponent(reference)}`),
  // --- webshop (denetim B5): v3'ten push edilen üç tablonun CRM'deki okuma yüzü -------------
  webOrders: (params: { state?: string; page?: number; pageSize?: number } = {}) =>
    request<Paged<WebOrderRecord>>(`/webshop/orders?${search(params)}`),
  webOrder: (reference: string) => request<WebOrderDetailRecord>(`/webshop/orders/${encodeURIComponent(reference)}`),
  // --- web siparişi belgeleri ve müşteri portalına gönderim (30.09.2026) --------------------
  webOrderDocuments: (reference: string) =>
    request<WebOrderDocumentList>(`/documents?webOrderReference=${encodeURIComponent(reference)}`),
  uploadWebOrderDocument: (body: {
    webOrderReference: string;
    webOrderItemReference?: string;
    kind: string;
    customerVisible: boolean;
    replacesDocumentId?: string;
    fileName: string;
    contentType: string;
    contentBase64: string;
  }) => request<WebOrderDocument>('/documents', { method: 'POST', body: JSON.stringify(body) }),
  setWebOrderDocumentVisibility: (id: string, visible: boolean) =>
    request<WebOrderDocument>(`/documents/${encodeURIComponent(id)}/customer-visibility`, {
      method: 'PUT',
      body: JSON.stringify({ visible }),
    }),
  retryWebOrderDocumentPush: (id: string) =>
    request<WebOrderDocument>(`/documents/${encodeURIComponent(id)}/v3-push/retry`, { method: 'POST', body: '{}' }),
  deleteWebOrderDocument: (id: string) =>
    request<{ id: string; deleted: boolean; withdrawing: boolean }>(`/documents/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  webSupportCases: (params: { state?: string; page?: number; pageSize?: number } = {}) =>
    request<Paged<WebSupportCaseRecord>>(`/webshop/support-cases?${search(params)}`),
  webSupportCase: (reference: string) => request<WebSupportCaseRecord>(`/webshop/support-cases/${encodeURIComponent(reference)}`),
  /** Uç en az birini istiyor; ikisi de boşsa liste değil `null` dönüyor (`webshop.service.ts`, `findCustomer`). */
  findWebCustomer: (params: { email?: string; externalId?: string }) =>
    request<WebCustomerRecord | null>(`/webshop/customers?${search(params)}`),

  // --- siteden gelen kayıt ana kayda bağlanır (Ürün Sahibi kararı, 27.08.2026) ---------------
  webCustomerReviews: (params: { state?: string; page?: number; pageSize?: number } = {}) =>
    request<WebCustomerReviewList>(`/webshop/customers/reviews?${search(params)}`),
  linkWebCustomer: (externalId: string, body: { customerId: string; note?: string }) =>
    request<{ externalId: string; customerId: string | null; reviewState: string }>(
      `/webshop/customers/${encodeURIComponent(externalId)}/link`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),
  // --- besinci kenar: on talep bekleme listesi -----------------------------------------------
  webDemands: (
    params: {
      followUpState?: string;
      productSlug?: string;
      state?: string;
      /** Tek bir talebin numarasi; WhatsApp teklifinde `WAT-<kimlik>` (02.10.2026). */
      reference?: string;
      page?: number;
      pageSize?: number;
    } = {},
  ) =>
    request<WebDemandList>(`/webshop/demands?${search(params)}`),
  workWebDemand: (reference: string, body: { followUpState: string; note: string }) =>
    request<{ reference: string; followUpState: string }>(
      `/webshop/demands/${encodeURIComponent(reference)}`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),

  resolveWebCustomerReview: (externalId: string, body: { note: string; unlink?: boolean }) =>
    request<{ externalId: string; customerId: string | null; reviewState: string }>(
      `/webshop/customers/${encodeURIComponent(externalId)}/review`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),

  // --- CRM-A13 ------------------------------------------------------------
  // Tedarikçi kartı Finans'ın, okuması Operasyon'da da var. Kullanıcı yönetiminde silme çağrısı
  // yoktur ve olmayacak: karşılığı `setUserStatus(id, false)`.
  suppliers: (params: { kind?: string; status?: string; workMode?: string; pageSize?: number } = {}) =>
    request<SupplierList>(`/finance/suppliers?${search(params)}`),
  supplierQuestions: (params: { status?: string; supplierId?: string } = {}) =>
    request<{ items: SupplierQuestion[]; total: number }>(`/finance/suppliers/questions?${search(params)}`),

  // --- CRM-B6: tedarikçi iptalleri ----------------------------------------
  supplierCancellations: (params: { state?: string; page?: number; pageSize?: number } = {}) =>
    request<SupplierCancellationList>(`/operations/supplier-cancellations?${search(params)}`),
  supplierCancellation: (id: string) => request<SupplierCancellation>(`/operations/supplier-cancellations/${id}`),
  reportSupplierCancellation: (body: {
    orderReference: string;
    customerId: string;
    supplierCode: string;
    productId?: string;
    departureId?: string;
    departureOn?: string;
    reason: string;
  }) => request<SupplierCancellation & { duplicate: boolean }>('/operations/supplier-cancellations', { method: 'POST', body: JSON.stringify(body) }),
  decideSupplierCancellation: (id: string, body: { outcome: 'SUBSTITUTED' | 'CANCELLED'; note: string; replacementOrderReference?: string }) =>
    request<SupplierCancellation>(`/operations/supplier-cancellations/${id}/decision`, { method: 'POST', body: JSON.stringify(body) }),

  // --- Operasyon: transfer isleri (09.10.2026) ----------------------------
  // Is acma yok: v3 odenmis siparisi bildirince is kendiliginden acilir. Operator listeler, sofor atar, tamamlar.
  transferJobs: (params: TransferJobQuery = {}) => request<TransferJobList>(`/operations/transfer-jobs?${search(params)}`),
  assignTransferJob: (id: string, body: { driverName: string; vehiclePlate?: string; note?: string }) =>
    request<TransferJob>(`/operations/transfer-jobs/${id}/assignment`, { method: 'POST', body: JSON.stringify(body) }),
  unassignTransferJob: (id: string) => request<TransferJob>(`/operations/transfer-jobs/${id}/unassignment`, { method: 'POST', body: '{}' }),
  completeTransferJob: (id: string) => request<TransferJob>(`/operations/transfer-jobs/${id}/completion`, { method: 'POST', body: '{}' }),

  // --- CRM-B7: vize talepleri ----------------------------------------------
  // `ReportVisaRequestDto`e karşılık gelen bir çağrı yok: v3'ün makine ucu (`operasyon/vize-talepleri`)
  // yalnız v3'ten geliyor, operatör talep açmıyor (`VisaMachineController`in kendi belgesi: "Report
  // only. v3 opens the case").
  visaRequests: (params: { state?: string; orderReference?: string; page?: number; pageSize?: number } = {}) =>
    request<VisaRequestList>(`/operations/visa-requests?${search(params)}`),
  visaDocuments: (id: string) => request<VisaDocumentList>(`/operations/visa-requests/${encodeURIComponent(id)}/documents`),
  previewVisaDocument: visaPreviewBlob,
  downloadVisaDocument: (id: string, documentId: string) => download(`/operations/visa-requests/${encodeURIComponent(id)}/documents/${encodeURIComponent(documentId)}/content`, 'vize-belgesi', true, /filename="(vize-belgesi-[0-9]+\.(?:pdf|jpg|png|webp))"/),
  visaRequest: (id: string) => request<VisaRequest>(`/operations/visa-requests/${id}`),
  submitVisaRequest: (id: string) => request<VisaRequest>(`/operations/visa-requests/${id}/submission`, { method: 'POST', body: '{}' }),
  decideVisaRequest: (id: string, body: { outcome: 'ISSUED' | 'REJECTED'; note: string; supplierReference?: string; chargedMinor?: number }) =>
    request<VisaRequest>(`/operations/visa-requests/${id}/decision`, { method: 'POST', body: JSON.stringify(body) }),
  addVisaExtraCharge: (id: string, body: { feeCode: string; quantity?: number; amountMinor?: number }) =>
    request<{ costEntryId: string; feeCode: string; quantity: number; amountMinor: number; currency: string; contractUnitMinor: number }>(
      `/operations/visa-requests/${id}/extra-charges`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  // --- CRM-B8: hizmet tamamlama (bitiş tarihi olmayan üründe GeziPuan olgunlaştırma) ------------
  markServiceCompleted: (body: { orderReference: string; completedOn?: string; note?: string }) =>
    request<ServiceCompletionResult>('/operations/service-completions', { method: 'POST', body: JSON.stringify(body) }),

  users: (params: { role?: string; status?: string; search?: string; pageSize?: number } = {}) =>
    request<CrmUserList>(`/users?${search(params)}`),
  /**
   * İK çalışan dizini. Süzme ve sıralama sunucuda: Türkçe katlama ve Türkçe harmanlama orada.
   *
   * Sayfalama parametresi yok ve olmaması ölçülmüş bir karar (`api/src/modules/hr/hr.dto.ts`).
   */
  ikDizin: (params: { search?: string; sort?: string; direction?: string; status?: string; role?: string } = {}) =>
    request<IkDizin>(`/hr/directory?${search(params)}`),
  assignRole: (id: string, body: { role: string; reason: string }) =>
    request<CrmUser & { fromRole: string; revokedSessions: number }>(`/users/${id}/role`, { method: 'PUT', body: JSON.stringify(body) }),
  setUserStatus: (id: string, body: { isActive: boolean; reason: string }) =>
    request<CrmUser & { revokedSessions: number }>(`/users/${id}/status`, { method: 'PUT', body: JSON.stringify(body) }),
  /** Parola yazılmaz: yanıttaki `setupUrl` davet bağlantısıdır, kullanıcı parolasını onunla koyar. */
  createUser: (body: { email: string; displayName: string; role: string }) =>
    request<CrmUser & { setupUrl: string; setupExpiresAt: string }>('/users', { method: 'POST', body: JSON.stringify(body) }),
  resetUserPassword: (id: string, body: { reason: string }) =>
    request<PasswordSetupLink & { id: string; revokedSessions: number }>(`/users/${id}/password-reset`, { method: 'POST', body: JSON.stringify(body) }),
  revokeUserSessions: (id: string) =>
    request<{ id: string; revokedSessions: number }>(`/users/${id}/sessions/revoke`, { method: 'POST', body: '{}' }),
  checkPasswordSetup: (token: string) =>
    request<{ displayName: string; purpose: 'INVITE' | 'RESET'; expiresAt: string }>('/auth/password-setup/check', { method: 'POST', body: JSON.stringify({ token }) }),
  completePasswordSetup: (token: string, password: string) =>
    request<{ ok: true }>('/auth/password-setup', { method: 'POST', body: JSON.stringify({ token, password }) }),
  changeOwnPassword: (currentPassword: string, newPassword: string) =>
    request<{ revokedSessions: number }>('/auth/change-password', { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) }),

  // --- CRM-A24: KVKK rıza defteri ve pazarlama denetim izi -----------------
  //
  // Kişiyi tanımlayan alan **hiçbir zaman** sorgu dizesinde: geçmiş ve durum sorguları okuma
  // olduğu hâlde POST. Gerekçesi sunucudakiyle aynı (`consent.dto.ts`): bir adres URL'e girdiği
  // anda yol boyunca her erişim kaydına yazılır ve rıza kaydının varlık sebebi tam olarak bunu
  // denetlemek. Liste ve günlük uçları kişi taşımadığı için GET kalıyor.
  consentTexts: (params: { channel?: string; locale?: string } = {}) =>
    request<{ items: ConsentText[]; total: number }>(`/marketing/consent/texts?${search(params)}`),
  publishConsentText: (body: {
    channel: string;
    version: string;
    locale: string;
    summary: string;
    body: string;
    effectiveFrom?: string;
  }) => request<ConsentText>('/marketing/consent/texts', { method: 'POST', body: JSON.stringify(body) }),
  consentLedger: (params: { channel?: string; status?: string; page?: number; pageSize?: number } = {}) =>
    request<ConsentLedgerPage>(`/marketing/consent?${search(params)}`),
  consentHistory: (body: { subject: string; channel?: string }) =>
    request<ConsentHistory>('/marketing/consent/history', { method: 'POST', body: JSON.stringify(body) }),
  consentStatus: (body: { subject: string; channel: string }) =>
    request<ConsentStatus>('/marketing/consent/status', { method: 'POST', body: JSON.stringify(body) }),
  withdrawConsent: (body: { subject: string; channel: string; note?: string; source?: string }) =>
    request<ConsentRecord>('/marketing/consent/withdraw', { method: 'POST', body: JSON.stringify(body) }),
  segmentRecipients: (body: { channel: string; campaignRef?: string; countryCode?: string; tagCode?: string }) =>
    request<SegmentRecipients>('/marketing/segments/recipients', { method: 'POST', body: JSON.stringify(body) }),
  marketingLog: (params: { action?: string; channel?: string; campaignRef?: string; page?: number; pageSize?: number } = {}) =>
    request<MarketingLogPage>(`/marketing/log?${search(params)}`),

  /**
   * Satış akışı vakası madde 14/15: "sepeti olan müşteri" segmenti.
   *
   * `cartSegment` yalnız sayar (`AbandonedCartSegment`, isim ve adres taşımaz — `segmentRecipients`
   * ile aynı gizlilik kuralı). `dispatchCartRecovery` gönderir; kanal yapılandırılmamışsa
   * `delivery.state === 'NOT_CONFIGURED'` döner ve hiçbir ağ çağrısı denenmez.
   */
  cartSegment: (params: { countryCode?: string; languageCode?: string; statusCode?: string; tagCode?: string; customerType?: string } = {}) =>
    request<AbandonedCartSegment>(`/marketing/segments/cart?${search(params)}`),
  dispatchCartRecovery: (body: {
    channel: string;
    campaignRef?: string;
    countryCode?: string;
    languageCode?: string;
    statusCode?: string;
    tagCode?: string;
    customerType?: string;
    dryRun?: boolean;
  }) => request<CartRecoveryDispatchResult>('/marketing/segments/cart/dispatch', { method: 'POST', body: JSON.stringify(body) }),

  /**
   * Denetim B10: bülten/newsletter yönetimi ekranı. Üç uç da personel tarafı, izin
   * `newsletter:read` (`api/src/modules/marketing/marketing.module.ts`). Makine tarafı
   * (`pazarlama/bulten/*`, v3'ün kullandığı) bunun dışında, ayrı bir sözleşme.
   */
  newsletterSubscribers: (params: { state?: string; page?: number; pageSize?: number } = {}) =>
    request<NewsletterSubscriberPage>(`/marketing/newsletter?${search(params)}`),
  newsletterReconciliations: (params: { limit?: number } = {}) =>
    request<NewsletterReconciliationPage>(`/marketing/newsletter/reconciliations?${search(params)}`),
  newsletterCadenceStatus: () =>
    request<NewsletterCadenceStatus>('/marketing/newsletter/mutabakat/durum'),

  /**
   * Denetim B11: bağımsız "Tüm denetim kaydı" ekranı. `GET /audit`, `AuditService.list()`in
   * sayfaladığı bütün denetim defteri (`api/src/modules/audit/audit.module.ts`, izin
   * `audit:read`). `oversight()` çağrısındaki `auditTrail` tek bir siparişe bağlıydı; bu uç
   * hiçbir siparişle sınırlı değil.
   */
  auditLog: (params: { action?: string; targetType?: string; page?: number; pageSize?: number } = {}) =>
    request<Paged<AuditRecord>>(`/audit?${search(params)}`),
};

/** CRM-A24. Sunucudaki `ConsentService`in döndürdüğü biçimlerin aynısı. */
export interface ConsentText {
  id: string;
  channel: string;
  version: string;
  locale: string;
  summary: string;
  body: string;
  bodyDigest: string;
  effectiveFrom: string;
  publishedBy: string;
  /** Yalnız liste ucunda: bu sürüm şu anda yürürlükte mi. */
  inForce?: boolean;
}

export interface ConsentRecord {
  id: string;
  subjectKey: string;
  customerId: string | null;
  channel: string;
  status: string;
  textVersionId: string;
  occurredAt: string;
  recordedAt: string;
  source: string;
  sourcePage: string | null;
  ip: string | null;
  userAgent: string | null;
  language: string;
  verifiedAt: string | null;
  decidedBy: string;
  recordedBy: string;
  note: string | null;
  text?: { version: string; locale: string; summary?: string; bodyDigest?: string };
}

export interface ConsentLedgerPage {
  items: ConsentRecord[];
  total: number;
  page: number;
  pageSize: number;
  byStatus: Record<string, number>;
  note: string;
}

export interface ConsentStatus {
  subjectKey: string;
  channel: string;
  sendable: boolean;
  basis: string;
  evidence: string;
  status: string | null;
  since: string | null;
  textVersionId: string | null;
  blockedBy: string | null;
}

export interface ConsentHistory {
  current: (Partial<ConsentStatus> & { channel: string })[];
  items: ConsentRecord[];
  total: number;
  truncated: boolean;
}

export interface SegmentRecipients {
  channel: string;
  campaignRef: string | null;
  candidates: number;
  allowed: number;
  blocked: number;
  blockedBy: Record<string, number>;
  evidence: Record<string, number>;
  truncated: boolean;
}

export interface MarketingLogPage {
  items: {
    id: string;
    occurredAt: string;
    actorId: string | null;
    actorRole: string | null;
    action: string;
    targetType: string;
    targetId: string | null;
    outcome: string;
    metadata: unknown;
  }[];
  total: number;
  page: number;
  pageSize: number;
  note: string;
}

/**
 * Denetim B10. `MarketingService.newsletter()`in döndürdüğü biçimin aynısı
 * (`api/src/modules/marketing/marketing.service.ts`). `PENDING` listelenir ama abone sayılmaz;
 * yalnız formu, kitleyi değil.
 */
export interface NewsletterSubscriberPage {
  items: {
    id: string;
    email: string;
    state: string;
    consentAt: string | null;
    consentSource: string | null;
    sourcePage: string | null;
    unsubscribedAt: string | null;
    suppressedAt: string | null;
    suppressReason: string | null;
    customerId: string | null;
    lastEventAt: string;
    writtenByClient: string;
  }[];
  total: number;
  page: number;
  pageSize: number;
  byState: Record<string, number>;
  feed: {
    owner: string;
    reportedBy: string;
    lastEventAt: string | null;
    lastReconciliation: { ranAt: string; clean: boolean; missing: number; diverged: number } | null;
    detail: string;
  };
}

/** Denetim B10. `NewsletterService.reconciliations()`in döndürdüğü biçimin aynısı. */
export interface NewsletterReconciliationPage {
  items: {
    id: string;
    ranAt: string;
    byClient: string;
    window: { from: string; to: string };
    claimed: number;
    matched: number;
    missing: number;
    diverged: number;
    clean: boolean;
    differences: unknown;
  }[];
  total: number;
}

/**
 * Denetim B10. `reconciliationCadenceStatus()`in döndürdüğü biçimin aynısı
 * (`api/src/modules/marketing/reconciliation-cadence.ts`). `lastRunAt` null ise hiç tur
 * koşmamış demektir, `hoursSinceLastRun` de bu yüzden null olur.
 */
export interface NewsletterCadenceStatus {
  lastRunAt: string | null;
  hoursSinceLastRun: number | null;
  cadenceHours: number;
  overdue: boolean;
}

/**
 * Denetim B11. `AuditService.list()`in döndürdüğü satırın aynısı
 * (`api/src/modules/audit/audit.service.ts`) — sipariş bazlı `Oversight.auditTrail`in aksine
 * bütün denetim defterini sayfalayarak okuyor, tek bir siparişe bağlı değil.
 */
export interface AuditRecord {
  id: string;
  occurredAt: string;
  actorId: string | null;
  actorRole: string | null;
  action: string;
  targetType: string;
  targetId: string | null;
  outcome: string;
  requestId: string | null;
  metadata: unknown;
}

/**
 * Satış akışı vakası madde 16: operasyon takvimi (`calendar.service.ts`, `CalendarEntry`in aynısı).
 *
 * `startsAt`/`endsAt` yalnız `dated` içindeki satırlarda dolu; `undated` içindeki her satırda
 * ikisi de `null`dur ve bu bir eksik veri değil, kalemin doğasıdır (ör. 30 gün tek giriş vize).
 */
export interface CalendarEntry {
  orderItemId: string;
  orderId: string;
  orderCode: string;
  orderStatus: string;
  customerName: string;
  itemType: string;
  description: string;
  supplierName: string;
  quantity: number;
  taskId: string | null;
  taskStatus: string | null;
  kind: 'RANGE' | 'DAY' | 'TIMED' | 'UNDATED';
  startsAt: string | null;
  endsAt: string | null;
}

export interface OperationsCalendar {
  from: string;
  to: string;
  dated: CalendarEntry[];
  undated: CalendarEntry[];
  undatedCount: number;
  total: number;
}

/**
 * Satış akışı vakası madde 17: tedarikçi bildirim durumu (`supplier-notification.service.ts`).
 * Kayıt yoksa uç sentetik bir varsayılan üretir: `channel: 'NOT_CONFIGURED'`, `state: 'PENDING'`.
 * `version` kayıt yoksa 0'dır — ilk `markSupplierNotified` çağrısı `expectedVersion` göndermeden yapılabilir.
 */
export interface SupplierNotificationRow {
  orderItemId: string;
  orderId: string;
  orderCode: string;
  itemType: string;
  description: string;
  supplierName: string;
  channel: 'NOT_CONFIGURED' | 'MANUAL';
  state: 'PENDING' | 'SENT';
  sentAt: string | null;
  sentBy: string | null;
  note: string | null;
  version: number;
}

/**
 * Satış akışı vakası madde 18: bir kalem için tedarikçi maliyeti girildi mi. **Tutar yok** —
 * 21.08.2026 kararı ("maliyetler operasyona gösterilmez") bu tipte de uygulanıyor: yalnız bir
 * bayrak (`hasCommittedCost`) ve sayaç, hiçbir `amountMinor` alanı yok.
 */
export interface SupplierCostStatus {
  orderItemId: string;
  supplierName: string;
  hasCommittedCost: boolean;
  entriesCount: number;
  lastEnteredAt: string | null;
}

/**
 * Kampanya akışı vakası madde 17-26: influencer hakedişi (`influencer-earning.service.ts`).
 *
 * Yedi durum bir çizgi üstünde ilerliyor: ACCRUED -> RECONCILIATION_SENT -> (DISPUTED ->)
 * APPROVED -> INVOICE_UPLOADED -> PAYMENT_ORDERED -> PAID. Ekran, bir satırın hangi eylemi
 * kabul ettiğini bu sıradan çıkarıyor, uç zaten reddediyor ama panel de göstermiyor.
 */
export interface InfluencerEarning {
  id: string;
  externalEarningId: string;
  externalInfluencerId: string;
  influencerName: string;
  externalCampaignId: string;
  campaignTitle: string;
  conversionType: string;
  periodStart: string;
  periodEnd: string;
  countedEvents: number;
  amountMinor: number;
  currencyCode: string;
  computedAt: string;
  status: string;
  reconciliationSentAt: string | null;
  reconciliationDueAt: string | null;
  disputedAt: string | null;
  disputeReason: string | null;
  disputeResolvedAt: string | null;
  disputeResolution: string | null;
  approvedAt: string | null;
  invoiceUploadedAt: string | null;
  paymentOrderReference: string | null;
  paymentOrderNote: string | null;
  paymentOrderedAt: string | null;
  paidAt: string | null;
  paidNote: string | null;
  version: number;
}

export interface InfluencerEarningListPage {
  items: InfluencerEarning[];
  total: number;
  page: number;
  pageSize: number;
}

export interface InfluencerEarningInvoice {
  id: string;
  earningId: string;
  fileName: string;
  contentType: string;
  byteSize: number;
  sha256: string;
  note: string | null;
  uploadedAt: string;
  storageBackend: string;
}

export interface InfluencerEarningDetail {
  earning: InfluencerEarning;
  invoices: InfluencerEarningInvoice[];
}

/**
 * Satış akışı vakası madde 19: `OrderInvoice`in Finans ekranındaki karşılığı. PayPartner
 * entegrasyonuna yazmıyor, yalnız zaten kesilmiş (ya da denenmiş) satırı okuyor.
 */
export interface OrderInvoiceRow {
  id: string;
  orderId: string;
  orderCode: string;
  customerName: string | null;
  state: string;
  invoiceNumber: string | null;
  currencyCode: string;
  amountMinor: number;
  requestedAt: string;
  issuedAt: string | null;
  lastError: string | null;
}

export type InvoiceQueueState = 'KESILDI' | 'ALIS_BEKLIYOR' | 'ENGELLI' | 'KESIM_KAPALI' | 'HAZIR';

export interface InvoiceQueueRow {
  reference: string;
  customerName: string;
  currencyCode: string;
  totalMinor: number;
  paidAt: string | null;
  queueState: InvoiceQueueState;
  missing: string[];
  lineCount: number;
  linesWithPurchase: number;
  purchaseRecorded: boolean;
  payableAedMinor: number | null;
  vatAedMinor: number | null;
  salesInvoiceState: string | null;
  invoiceNumber: string | null;
}

export interface InvoiceQueuePage {
  items: InvoiceQueueRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface PurchaseInvoiceInput {
  itemReference: string;
  costKind: 'SUPPLIER_SERVICE' | 'GOVERNMENT_FEE';
  supplierName: string;
  supplierInvoiceNumber: string;
  invoiceDate: string;
  currencyCode: string;
  amountMinor: number;
  fxRateMicros?: number;
  inputVatAedMinor?: number;
  vatRateBps?: number;
  note?: string;
}

export interface PurchaseInvoiceRow extends PurchaseInvoiceInput {
  id: string;
  amountAedMinor: number;
  documentId: string | null;
  recordedAt: string;
  voidedAt: string | null;
  voidReason: string | null;
}

export interface WebSalesInvoiceLine {
  kind: 'SERVICE' | 'DISBURSEMENT' | 'DISCOUNT';
  itemReference: string | null;
  description: string;
  amountMinor: number;
  taxRatePercent: number;
  vatMinor: number;
}

export interface SalesInvoiceRecordInput {
  invoiceNumber: string;
  invoiceDate: string;
  sellerEntity: 'AE' | 'TR';
  currencyCode: string;
  payableMinor: number;
  vatMinor: number;
  vatRateBps?: number;
  fxRateMicros?: number;
  externalUrl?: string;
  note?: string;
}

export interface PurchaseInvoiceListPage {
  items: (PurchaseInvoiceRow & { orderReference: string; voidedAt: string | null })[];
  total: number;
  page: number;
  pageSize: number;
}

export interface InvoiceMarginRow {
  reference: string;
  customerName: string;
  orderCurrencyCode: string;
  orderTotalMinor: number;
  purchaseCount: number;
  purchaseNetAedMinor: number;
  salesInvoiceNumber: string | null;
  sellerEntity: string | null;
  salesNetAedMinor: number | null;
  marginAedMinor: number | null;
  marginPercentBps: number | null;
  purchaseNetUsdMinor: number | null;
  salesNetUsdMinor: number | null;
  marginUsdMinor: number | null;
  note: 'SATIS_FATURASI_YOK' | 'ALIS_FATURASI_YOK' | null;
}

export interface InvoiceMarginPage {
  items: InvoiceMarginRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface WebSalesInvoice {
  id: string;
  state: 'PENDING' | 'ISSUED' | 'FAILED';
  source: 'PAYPARTNER_API' | 'MANUAL';
  sellerEntity: 'AE' | 'TR';
  externalUrl: string | null;
  documentId: string | null;
  vatRateBps: number | null;
  note: string | null;
  invoiceDate: string;
  currencyCode: string;
  fxRateMicros?: number | null;
  invoiceNumber: string | null;
  payableMinor: number;
  vatMinor: number;
  lines: WebSalesInvoiceLine[];
  alreadyExisted: boolean;
  attemptCount: number;
  lastError: string | null;
  issuedAt: string | null;
}

export interface InvoiceQueueDetail {
  reference: string;
  orderState: string;
  customerName: string;
  currencyCode: string;
  totalMinor: number;
  paidAt: string | null;
  proformaNumber: string | null;
  purchaseRecorded: boolean;
  billingSource: string;
  billingIdentity: { visible: boolean; nationalId: string | null; passportNumber: string | null; phone: string | null; countryCode: string | null; address: string | null } | null;
  invoiceFx: { rateMicros: number; source: string | null; date: string | null } | null;
  queueState: InvoiceQueueState;
  gates: { key: string; met: boolean; detail: string }[];
  missing: string[];
  preview: {
    currencyCode: string;
    lines: WebSalesInvoiceLine[];
    grossMinor: number;
    discountMinor: number;
    payableMinor: number;
    disbursementMinor: number;
    vatMinor: number;
  } | null;
  lines: {
    itemReference: string;
    productId: string;
    productKind: string | null;
    title: string | null;
    quantity: number;
    unitAmountMinor: number;
    purchaseInvoices: PurchaseInvoiceRow[];
  }[];
  salesInvoice: WebSalesInvoice | null;
}

export interface OrderInvoiceListPage {
  items: OrderInvoiceRow[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * Satış akışı vakası madde 20: bir `CostEntry` (COMMITTED) ve varsa üstündeki ödeme hazırlığı.
 * `paymentStatus` uçtan geliyor: `UNPAID` (ödeme emri yok), `PAYMENT_ORDERED`, `PAID`.
 */
export interface SupplierPayableCost {
  costEntryId: string;
  orderId: string | null;
  orderCode: string | null;
  orderItemId: string | null;
  externalOrderReference: string | null;
  supplierName: string;
  description: string;
  amountMinor: number;
  currencyCode: string;
  recordedAt: string;
  paymentStatus: 'UNPAID' | 'PAYMENT_ORDERED' | 'PAID';
  payment: {
    id: string;
    reference: string;
    note: string | null;
    orderedAt: string;
    paidAt: string | null;
    paidNote: string | null;
  } | null;
}

export interface SupplierPayableListPage {
  items: SupplierPayableCost[];
  total: number;
  page: number;
  pageSize: number;
}

export interface SupplierPayment {
  id: string;
  costEntryId: string;
  status: 'PAYMENT_ORDERED' | 'PAID';
  amountMinor: number;
  currencyCode: string;
  paymentOrderReference: string;
  paymentOrderedAt: string;
  paidAt: string | null;
}

export interface SupplierExpenseInvoice {
  id: string;
  costEntryId: string;
  fileName: string;
  contentType: string;
  byteSize: number;
  sha256: string;
  note: string | null;
  uploadedAt: string;
  storageBackend: string;
}

export interface SupplierPaymentDetail {
  cost: SupplierPayableCost;
  invoices: SupplierExpenseInvoice[];
}

/**
 * Kupon akışı vakası §5: influencer kampanyası kuponu (`coupon.service.ts`, `toDto`in aynısı).
 * Yalnız Finans yayınlar/yeniden yayınlar; kupon budget'i (kalan hak) burada görünür.
 */
/** Sabit tutar `{ amountMinor, currency }` (eski biçim, değişmedi) ya da yüzde (24.10.2026). */
export interface CouponDiscountView {
  kind?: 'PERCENT';
  amountMinor?: number | null;
  currency?: string | null;
  percentBasisPoints?: number | null;
  maxDiscountMinor?: number | null;
}

export interface Coupon {
  financeCouponId: string;
  code: string;
  influencerId: string | null;
  campaignId: string | null;
  source?: 'INFLUENCER' | 'INTERNAL';
  discount: CouponDiscountView;
  scope: unknown;
  validFrom: string;
  validUntil: string;
  status: string;
  budget: { maxRedemptions: number | null; confirmed: number; outstandingHolds: number; remaining: number | null };
  publication: { state: string; publishedAt: string | null; error: string | null };
}

export interface CouponListPage {
  items: Coupon[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CouponReservation {
  id: string;
  orderId: string;
  customerId: string | null;
  channel: string;
  state: string;
  discountAmountMinor: number;
  currency: string;
  basketAmountMinor: number | null;
  expiresAt: string;
  confirmedAt: string | null;
  releasedAt: string | null;
  releaseReason: string | null;
  requestedByClient: string;
  settledByClient: string | null;
}

export interface CouponDetail extends Coupon {
  reservations: CouponReservation[];
}

/** Influencer platformuna borçlu kalınan kupon bildirimleri (kupon akışı §5.3). */
export interface CouponNotificationRow {
  id: string;
  reservationId: string;
  outcome: string;
  payload: unknown;
  attempts: number;
  lastAttemptAt: string | null;
  lastError: string | null;
  createdAt: string;
}

/** `CouponNotificationDeliverer.drain()`in raporu. Alan adlari ucun dondurdugu adlar. */
export interface CouponDrainReport {
  configured: boolean;
  claimed: number;
  sent: number;
  failed: number;
  abandoned: number;
  busy?: boolean;
}

export interface CouponNotificationPage {
  items: CouponNotificationRow[];
  /** On denemede pes edilmis satirlar. Sayac degil, satirlarin kendisi (`coupon.service.ts`). */
  abandoned: CouponNotificationRow[];
  total: number;
  failed: number;
  delivered: number;
  oldestPendingAt: string | null;
  delivery: { state: 'CONFIGURED' | 'NOT_CONFIGURED'; detail: string };
}

/** Şirketin üyelere GeziPuan olarak borçlu olduğu toplam (`gezipuan.service.ts`, `liability()`). */
export interface GezipuanLiability {
  currency: 'EUR';
  outstandingMinor: number;
  pendingMinor: number;
  committedMinor: number;
  expiringWithin30DaysMinor: number;
  heldMinor: number;
  membersWithBalance: number;
  unsweptExpiredCredits: number;
  asOf: string;
}

/**
 * Satış akışı vakası madde 14: "sepeti olan müşteri" segmentinin ölçümü.
 *
 * `segmentRecipients` ile aynı gizlilik kuralı: **sayar, insan göstermez.** İsim, e-posta,
 * telefon hiçbiri buradan dönmez; kişi listesi yalnız gönderim anında (`dispatchCartRecovery`
 * içinde) okunur ve HTTP cevabına hiç girmez.
 */
export interface AbandonedCartSegment {
  criteria: unknown;
  waitingThreshold: { idleMinutes: number; cutoff: string; detail: string };
  totalQuotes: number;
  totalCustomers: number;
  waiting: { oldestExpiredAt: string | null; newestExpiredAt: string | null; avgWaitingDays: number | null };
  breakdown: {
    byCountry: { countryCode: string | null; count: number }[];
    byLanguage: { languageCode: string | null; count: number }[];
    byCustomerType: { type: string; count: number }[];
    byItemType: { itemType: string; count: number }[];
    byCurrency: { currencyCode: string; count: number; totalMinor: number }[];
  };
  discountSource: { mechanism: string; withManualDiscount: number; detail: string };
  delivery: { state: string; detail: string };
  consent: { enforcedAt: string; detail: string };
}

/**
 * Satış akışı vakası madde 15: sepet hatırlatma gönderiminin sonucu.
 *
 * Kanal e-posta değilse (`WHATSAPP`/`SMS`), sağlayıcı henüz seçilmedi ve `dispatched` alanları
 * hiç yok — yalnız `delivery.state === 'NOT_CONFIGURED'` ve nedeni. Bu, ekranın **açıkça**
 * göstermesi gereken hâl; sessizce düşen bir gönderim değil.
 */
export interface CartRecoveryDispatchResult {
  channel: string;
  campaignRef: string | null;
  candidates: number;
  dispatched?: number;
  dryRun?: boolean;
  skippedNoAddress?: number;
  blockedByConsent?: number;
  notConfigured?: number;
  failed?: number;
  blockedBy?: Record<string, number>;
  delivery: { state: 'CONFIGURED' | 'NOT_CONFIGURED'; detail: string };
  log?: string;
}


// --- Pazarlama: kampanyalar ve geri kazanım (07.10.2026) -----------------------------

export interface CampaignRow {
  kind: 'INTERNAL' | 'INFLUENCER';
  id: string;
  name: string | null;
  influencerRef: string | null;
  discount: CouponDiscountView;
  validFrom: string;
  validUntil: string;
  status: 'ACTIVE' | 'SUSPENDED';
  codeCount: number;
  confirmed: number;
  held: number;
  cap: number | null;
  remaining: number | null;
  budgetMinor: number | null;
  committedMinor: number | null;
  unpublishedCodes: number;
}

export interface CampaignCode {
  code: string;
  status: string;
  maxRedemptions: number | null;
  confirmed: number;
  publishState: string;
  validUntil: string;
  held?: number;
}

export interface CampaignDetail {
  campaign: CampaignRow;
  scope: { productIds?: string[]; departureFrom?: string; departureUntil?: string; minimumAmount?: number; currency?: string };
  allCatalogue: boolean;
  perCodeLimit: number;
  codes: CampaignCode[];
}

export interface CampaignCodesInput {
  mode: 'SINGLE' | 'BULK';
  code?: string;
  prefix?: string;
  count?: number;
}

export interface CreateCampaignInput {
  name: string;
  discountAmountMinor: number;
  currency: string;
  productIds?: string[];
  allCatalogue?: boolean;
  departureFrom?: string;
  departureUntil?: string;
  minimumAmountMinor?: number;
  perCodeLimit: number;
  budgetMinor?: number;
  validFrom: string;
  validUntil: string;
  codes?: CampaignCodesInput;
}

export type RecoveryTriggerKey = 'VISA_CART' | 'TOUR_CART' | 'HOTEL_CART';

export interface RecoveryTemplates {
  tr: { subject: string; html: string; text: string };
  en: { subject: string; html: string; text: string };
}

export interface RecoveryAutomationInput {
  enabled: boolean;
  dryRun: boolean;
  waitMinutes: number;
  maxAgeDays: number;
  frequencyDays: number;
  campaignId: string | null;
  templates: RecoveryTemplates;
  excludedEmails: string[];
}

export interface RecoveryAutomation extends RecoveryAutomationInput {
  trigger: RecoveryTriggerKey;
  configured: boolean;
  updatedBy: string | null;
  updatedAt: string | null;
}

export interface RecoveryOverview {
  readiness: { email: string | null; unsubscribe: string | null };
  items: Array<RecoveryAutomation & { stats: { sent: number; wouldSend: number; skipped: number; failed: number; couponUsed: number; paidAfterSend: number } }>;
}

export interface RecoveryHistoryRow {
  id: string;
  reference: string;
  language: string;
  productId: string;
  amountMinor: number;
  currency: string;
  cartCreatedAt: string;
  outcome: 'WOULD_SEND' | 'SENT' | 'FAILED' | 'SKIPPED' | 'CLAIMED';
  skipReason: string | null;
  error: string | null;
  couponCode: string | null;
  sentAt: string | null;
  couponUsed: boolean | null;
  cartPaid: boolean;
  cartState: string;
}

export interface RecoveryHistoryPage {
  page: number;
  pageSize: number;
  total: number;
  items: RecoveryHistoryRow[];
}

export interface RecoveryRunSummary {
  trigger: RecoveryTriggerKey;
  mode: 'OFF' | 'DRY' | 'LIVE';
  scanned: number;
  sent: number;
  wouldSend: number;
  skipped: number;
  failed: number;
  blocked: string | null;
}

// Pazarlama SEO: null ölçülmemiş değerdir, sıfır değildir.
export type SeoTargetKind = 'GATE' | 'PROPOSAL' | 'SCENARIO';
export type SeoProductKind = 'SALE' | 'VISA_SERVICE' | 'REQUEST_ONLY';
export type SeoReviewPlatform = 'GOOGLE_BUSINESS' | 'TRUSTPILOT';
export interface SeoTarget { value: number; kind: SeoTargetKind; approved: boolean; note: string | null }
export type SeoTargetUrlStatus = 'BEKLIYOR' | 'YENILENDI' | 'OLCULDU';
export interface SeoMainTarget {
  id: string; path: string; cluster: string; klass: 'A' | 'A3' | 'B' | 'C'; reachable: boolean;
  monthlyVolume: number | null; targetClicksPerDay: number | null; targetNote: string | null;
  mainKeyword: string | null; status: SeoTargetUrlStatus; clicks: number | null; impressions: number | null;
  avgPosition: number | null; windowDays: number | null; clicksPerDay: number | null;
  serpRank: number | null; rankChange: number | null; trend: 'UP' | 'DOWN' | 'FLAT' | 'FIRST' | null;
}
export interface SeoMainTargets {
  weekIso: string | null;
  program: { urlCount: number; measured: number; clicksPerDay: number | null;
    targets: { d30: SeoTarget | null; d60: SeoTarget | null; d90: SeoTarget | null };
    classCounts: Record<SeoMainTarget['klass'], number> };
  items: SeoMainTarget[];
}
export interface SeoRecognition { clicks: number; impressions: number; queries: number | null; definition: string }
export interface SeoKpis {
  lastRun: { id: string; weekIso: string; measuredAt: string; source: string } | null;
  items: { code: string; label: string; unit: string; source: string; basis: string; value: number | null; measured: boolean; previous: number | null; window: { from: string; to: string | null } | null; targets: { d30: SeoTarget | null; d60: SeoTarget | null; d90: SeoTarget | null }; gate: 'MET' | 'OPEN' | 'NOT_MEASURED' | null }[];
  gates: { total: number; met: number; notMeasured: number };
  definitions: (SeoRecognition & { group: string; from: string; to: string })[];
}
export type SeoGoalState = 'REACHED' | 'NEAR' | 'FAR' | 'NO_TARGET' | 'NO_READING';
export type SeoNewness = 'LATEST' | 'PREVIOUS' | 'ADDED';
export type SeoWorkType = 'RENEW' | 'NEW' | 'NONE';
/** undefined = dokunma, null = temizle. */
export interface SeoKeywordEdit { targetRank?: number | null; targetPage?: string | null; workType?: SeoWorkType | null }
export type SeoDfsStatus = 'RANKED' | 'OUTSIDE_100' | 'NOT_MEASURED';
export interface SeoSeriesPoint { weekIso: string; monthlyVolume: number | null; dfsRank: number | null; dfsStatus: SeoDfsStatus | null; gscAvgPosition: number | null; gscImpressions: number | null }
export interface SeoKeywordItem {
  id: string; term: string; market: string; language: string; cluster: string | null; productKind: SeoProductKind | null; workType: SeoWorkType | null; targetPage: string | null; score: number | null; targetRank: number | null;
  monthlyVolume: number | null; dfsRank: number | null; dfsStatus: SeoDfsStatus | null; gscAvgPosition: number | null; gscImpressions: number | null; change: number | null; firstMeasurement: boolean;
  goal: SeoGoalState; goalGap: number | null; readingCount: number; trendReady: boolean; firstSeenWeek: string | null; newness: SeoNewness | null; series: SeoSeriesPoint[];
}
export interface SeoKeywords {
  weekIso: string | null; hasPrevious: boolean; total: number; targeted: number; runCount: number; lastImportAt: string | null;
  items: SeoKeywordItem[];
}
export interface SeoMover { id: string; term: string; dfsRank: number | null; change: number; targetRank: number | null }
export interface SeoGroupTrend { key: string; keywords: number; points: { weekIso: string; avgRank: number | null; n: number }[] }
export interface SeoKeywordSummary {
  runCount: number; lastImportAt: string | null; lastWeekIso: string | null; weeks: string[];
  goals: { reached: number; near: number; far: number; noTarget: number; noReading: number };
  movers: { hasPrevious: boolean; up: SeoMover[]; down: SeoMover[]; unchanged: number };
  clusters: SeoGroupTrend[]; markets: SeoGroupTrend[];
  impressions: { weekIso: string; total: number | null; n: number }[];
  fresh: { id: string; term: string; newness: SeoNewness; firstSeenWeek: string | null; dfsRank: number | null; cluster: string | null }[];
}
export interface SeoIndex {
  weekIso: string | null;
  strata: { stratum: string; pool: number; sample: number; indexed: number; discovered: number; unknown: number; crawledNotIndexed: number; other: number; error: number; newestCrawl: string | null }[];
  totals: { sample: number; indexed: number } | null;
  previousTotals: { sample: number; indexed: number } | null;
  crawl: { windowFrom: string | null; windowTo: string | null; googlebotRequests: number; sitemapUrls: number; sitemapUrlsVisited: number; visitRate: number | null; fiveXxCount: number | null; avgSitemapSeconds: number | null; notes: string[] } | null;
}
export interface SeoLinkReading { source: string; referringDomains: number | null; qualityDomains: number | null; at: string }
export interface SeoLinks {
  links: { latest: SeoLinkReading | null; history: SeoLinkReading[]; measured: boolean };
  reviews: { platform: SeoReviewPlatform; reviewCount: number | null; rating: number | null; at: string | null; history: { reviewCount: number; at: string }[] }[];
  recognition: { brand: SeoRecognition | null; nonbrand: SeoRecognition | null };
}
export interface SeoPlan {
  weeks: { weekNo: number; startsOn: string; milestone: string; owners: string[]; status: 'PLANNED' | 'DONE' | 'LATE' }[];
  decisions: { id: string; title: string; question: string; owner: string; dueOn: string | null; status: 'OPEN' | 'DECIDED'; decision: string | null; decidedAt: string | null }[];
  runs: { id: string; weekIso: string; status: 'APPLIED' | 'SUPERSEDED'; source: string; receivedAt: string; rowCounts: Record<string, number> }[];
}
export interface SeoTargetInput { targetValue: number; kind: SeoTargetKind; approve?: boolean; note?: string }
export interface SeoKeywordInput { term: string; market?: string; language?: string; cluster?: string; productKind?: SeoProductKind; targetPage?: string; targetRank?: number }
export interface SeoKeywordQuery { q?: string; is?: 'yenileme' | 'yeni' | 'yok'; urun?: SeoProductKind; hedef?: 'var' | 'yok'; yeni?: '1' }

export interface SeoRatio { num: number; den: number; rate: number | null; n: number }
export interface SeoAiMetric { engine: string; mention: SeoRatio; voice: SeoRatio; rank: { avg: number | null; n: number }; sourceShare: SeoRatio }
export interface SeoCostWeek { weekIso: string; capUsd: number | null; spentUsd: number; usage: number | null; over: boolean; lines: { kind: string; provider: string; units: number; costUsd: number }[] }
export interface SeoCosts { current: SeoCostWeek | null; history: SeoCostWeek[] }
export interface SeoAiQuestion { id: string; text: string; label: string | null; branded: boolean }
export interface SeoAi {
  weekIso: string | null; questions: SeoAiQuestion[]; engines: string[];
  grid: { questionId: string; cells: { engine: string; status: 'ok' | 'no_surface' | 'error' | 'none'; lastAnswer?: string | null; prevAnswer?: string | null; sources?: string[]; brandMentioned?: boolean | null; rank?: number | null }[] }[];
  headline: SeoAiMetric | null; byEngine: SeoAiMetric[]; branded: SeoAiMetric | null;
  competitors: (SeoRatio & { name: string; domain: string })[];
  sources: { domain: string; citations: number; own: boolean }[]; opportunities: { domain: string; citations: number }[];
  readiness: { passed: number; total: number; measured: number; checks: { code: string; passed: boolean | null; note: string | null }[] } | null;
  costs: SeoCosts;
}
export interface SeoSearchTotals { searches: number; unserved: number; unservedRate: number | null }
export interface SeoSearchSummary {
  window: { from: string; to: string };
  traffic: 'human';
  totals: SeoSearchTotals;
  unservedByReason: { reason: string; searches: number }[];
  topQueries: { term: string; human: number; unverified: number; journeys: number }[];
  viewedNone: { term: string; searches: number }[];
  unmet: { term: string; searches: number; reason: string }[];
  scrubbedDropped?: number;
}
/** summary null: paketlerde arama blogu yok (ölçülmedi). */
export interface SeoSearch { weekIso: string | null; measuredAt: string | null; summary: SeoSearchSummary | null; previous: { weekIso: string; totals: SeoSearchTotals } | null; newTerms?: { topQueries: string[]; viewedNone: string[]; unmet: string[] } | null }
export interface SeoPosts { items: { id: string; url: string; title: string; focusTerm: string; publishedAt: string; focusTracked: boolean; rankHistory: { weekIso: string; dfsRank: number | null; gscAvgPosition: number | null }[]; questions: { id: string; text: string }[]; mention: SeoRatio }[] }
